import ipRegex from 'ip-regex';
import cloneDeep from 'lodash.clonedeep';
import _ from 'lodash';
import lodashTemplate from 'lodash.template';
import {
  FormTemplate,
  FormTemplateField,
  FormTemplateVisibilityProperties,
  FormTemplateFieldMap,
  FormTemplateMetaData,
  FormTemplateOption,
  FormTemplateSection,
} from 'data/template/template.types';
import { adaptTemplateFieldVisibility } from 'data/template/templateVisibility.utils';
import { Deployment, ValueLabelPair } from 'data/types';
import { getSortedTimeZoneValueLabels } from 'shared/timeZoneUtils/timeZone.utils';
import { Network } from 'data/network/network.types';
import { SecurityInfo, SecurityInfoFilter, SecurityInfoFilterByInterface } from 'data/securityInfo/securityInfo.types';
import { SupportedFeaturesAccessFunctions, SupportedFeaturesAccessFunction } from 'data/supportedFeatures/supportedFeatures.types';
import {
  AccessFunction,
  AccessFunctionFormData,
  AccessFunctionInterfaceFormData,
  AccessFunctionInterfaceFormSectionType,
  ACCESS_FUNCTION_REQ_STATE_FIELD,
  ACCESS_FUNCTION_STATE_FIELD,
} from 'data/accessFunction/accessFunction.types';
import { Xtp } from 'data/xtp/xtp.types';
import { Xdp } from 'data/xdp/xdp.types';
import { ProcessOption } from 'data/processOption/processOption.types';
import { SshSecurityInfo } from 'data/sshSecurityInfo/sshSecurityInfo.types';
import { Diameter } from 'data/diameter/diameter.types';
import { isHeaderSelectionPanel } from 'routes/AccessFunction/components/MultifacetedFormSection/MultifacetedForm.utils';
import { NodeData } from 'data/nodes/nodes.types';
import { DeliveryFunctionNetworkInterface } from 'data/deliveryFunctionNetworkInterface/deliveryFunctionNetworkInterface.types';
import { EMPTY_FIELD } from 'shared/form';
import { Buffer } from 'buffer';
import { Contact } from 'data/contact/contact.types';
import { CspConfig } from 'data/cspConfig/cspConfig.types';
import { fieldAffectsOtherFieldsOnChange } from './accessFunctionInterfaceTabFieldUpdate';
import { accessFunctionsAllowedWithoutInterfaces } from '../AccessFunction.types';
import { adaptTemplateAndDataFor5GAfsForGROnFieldUpdate } from './handle5gPOIsforGR';
import { adaptTemplateFor4GAfsForGROnFieldUpdate } from './handle4gPOIsforGR';

const POSSIBLE_INTERFACES = [
  'provisioningInterfaces',
  'provisioningInterfaces_directFromAF',
  'provisioningInterfaces_trafficManager',
  'dataInterfaces',
  'dataInterfaces_directFromAF',
  'dataInterfaces_trafficManager',
  'contentInterfaces',
  'contentInterfaces_directFromAF',
  'contentInterfaces_trafficManager',
];

const TEMPLATE_SETTINGS = {
  interpolate: /\{\{(.+?)\}\}/g,
};

const CSP_DEFAULT_OPTION: FormTemplateOption = {
  label: 'None',
  value: '',
};

type FormTemplateOptionMap = {
  default: FormTemplateOption[]
  [key: string]: FormTemplateOption[]
}

export function adaptTemplateOptions(
  templates: { [key: string]: FormTemplate },
  countries: string[],
  timezones: ValueLabelPair[],
  contacts: Contact[],
  networks: Network[],
  securityInfos: SecurityInfo[],
  sshSecurityInfos: SshSecurityInfo[],
  xtps: Xtp[],
  xdps: Xdp[],
  processOptions: ProcessOption[],
  enabledAccessFunctions: SupportedFeaturesAccessFunctions,
  xcipioNodes: NodeData[],
  accessFunctions: AccessFunction[],
  deliveryFunctionNetworkInterfaces: DeliveryFunctionNetworkInterface[],
  enabledFeatures?: string[],
  cspConfigs: CspConfig[] = [],
): { [key: string]: FormTemplate } {
  const adaptedTemplates: { [key: string]: FormTemplate } = {};

  const tagOptions = getTagOptions(templates, enabledAccessFunctions);
  const countryOptions = getFieldOptions(countries);
  const timezoneOptions = getTimeZoneFieldOptions(timezones);
  const contactOptions = getContactsOptions(contacts);
  const securityInfoOptions = getSecurityInfoOptions(securityInfos, []);
  const sshSecurityInfoOptions = getSshSecurityInfoOptions(sshSecurityInfos);
  const xtpIdOptions = getXtpIdOptions(xtps);
  const xdpIdOptions = getXdpIdOptions(xdps);
  const ccpagOptions = getXcipioNodesByType(xcipioNodes, ['CCPAG']);
  const xcf3Options = getXcipioNodesByType(xcipioNodes, ['XCF3']);
  const mdf2Options = getXcipioNodesByType(xcipioNodes, ['MDF2', 'XCP']);
  const mdf3Options = getXcipioNodesByType(xcipioNodes, ['MDF3', 'XCP']);
  const dfniOptions = getDFNIOptions(deliveryFunctionNetworkInterfaces);
  const cspOptions = getCspOptions(cspConfigs);

  const ericssonLimsOptions = getEricssonLimsOptions(accessFunctions);

  Object.keys(templates).forEach((templateName) => {
    const template = cloneDeep(templates[templateName]);

    if (enabledAccessFunctions[template.key] == null) {
      return;
    }

    const versionOptions = getSoftwareVersionOptions(template.key, enabledAccessFunctions);
    const model = getSoftwareModel(enabledAccessFunctions[template.key]);
    const httpVersions = getHTTPVersions(enabledAccessFunctions[template.key]);
    const afProtocolVersions = getAfProtocolVersions(enabledAccessFunctions[template.key]);
    const httpDefaultVersion = getHTTPDefaultVersion(enabledAccessFunctions[template.key]);

    setInitialValueFromProcessOptions(template, processOptions);

    setTagOptions(template, tagOptions);
    setLocationFieldOptions(template, countryOptions, 'country');
    setTimezoneOptions(template, timezoneOptions);
    setContactOptions(template, contactOptions);
    setOptionsForNetworkFields(template, networks, xcipioNodes);
    setOptionsForFields(template, securityInfoOptions, ['secinfoid']);
    setOptionsForFields(template, sshSecurityInfoOptions, ['sshInfoId']);
    setOptionsForFields(template, xdpIdOptions, ['ipduid']);
    setOptionsForFields(template, xtpIdOptions, ['lbid']);
    setOptionForTemplateSection(template.systemInformation as FormTemplateSection, versionOptions, ['version']);
    setCspDefaultOption(template.systemInformation as FormTemplateSection);
    setOptionForTemplateSection(template.systemInformation as FormTemplateSection, cspOptions, ['cspid']);
    setFieldInitialValueForTemplateSection(template.systemInformation as FormTemplateSection, model, ['model']);
    if (template.key === 'ETSI103221') {
      const modelNumbers = getModelNumbers(enabledAccessFunctions[template.key]);
      setOptionForTemplateSection(template.systemInformation as FormTemplateSection, modelNumbers, ['model']);
    }
    setOptionForOwnIpAddr(template, xdps);
    // setOptionsForFields(template, ccpagOptions, ['li_x1_ccpagnodename']);
    setOptionsForFields(template, ccpagOptions, ['ccnodename']);
    setOptionsForFields(template, mdf2Options, ['mdf2NodeName']);
    // setOptionsForFields(template, mdf2Options, ['li_x1_mdf2nodename']);
    setOptionsForFields(template, mdf2Options, ['mdf2NodeNameMultiSelect']);
    setOptionsForFields(template, mdf3Options, ['mdf3NodeName']);
    // setOptionsForFields(template, mdf3Options, ['li_x1_mdf3nodename']);
    setOptionsForLiX1Fields(template, accessFunctions, networks, ccpagOptions, xcf3Options, mdf2Options, mdf3Options);
    setOptionsForFields(template, mdf3Options, ['mdf3NodeNameMultiSelect']);
    setOptionsForFields(template, httpVersions, ['httpversion']);
    setOptionsForFields(template, httpVersions, ['HTTPVERSION']);
    if (afProtocolVersions) {
      setOptionForTemplateSection(template.provisioningInterfaces as FormTemplateSection, afProtocolVersions, ['version']);
    }
    setFieldInitialValueForTemplateSection(template.provisioningInterfaces as FormTemplateSection, httpDefaultVersion, ['httpversion']);
    setOptionsForFields(template, dfniOptions, ['niid']);

    if (template.key === 'ERICSSON_NE') {
      setOptionsForFields(template, ericssonLimsOptions, ['parentid']);
    }

    if (template.key === 'AXSGP') {
      const initialVersion = ((template?.systemInformation as FormTemplateSection).fields.version.initial as string) || '';
      setAxsgpModelOptions(template, getModelBasedOnVersion(templates.AXSGP, initialVersion));
    }

    if (template.key === 'MVNR_CSCF') {
      if (enabledFeatures?.includes('AF-MVNR-IMS-CMS')) {
        removeInterface(template, 'provisioningInterfaces_directFromAF');
        removeInterface(template, 'contentInterfaces_directFromAF');
        removeInterface(template, 'contentInterfaces_trafficManager');
        (template.general as FormTemplateSection).fields.contentDeliveryVia.hide = 'true';
      } else {
        removeInterface(template, 'contentInterfaces');
      }
    }

    if (template.key === 'MVNR_SBC') {
      if (enabledFeatures?.includes('AF-MVNR-IMS-CMS')) {
        removeInterface(template, 'provisioningInterfaces_directFromAF');
        removeInterface(template, 'contentInterfaces_directFromAF');
        removeInterface(template, 'contentInterfaces_trafficManager');
        (template.general as FormTemplateSection).fields.contentDeliveryVia.hide = 'true';
      } else {
        removeInterface(template, 'contentInterfaces');
      }
    }

    if (template.key === 'MITEL_TAS') {
      if (enabledFeatures?.includes('AF-MVNR-IMS-CMS')) {
        removeInterface(template, 'provisioningInterfaces');
      }
    }

    if (template.key === 'MVNR_MME') {
      if (enabledFeatures?.includes('AF-MVNR-PACO-CMS')) {
        removeInterface(template, 'provisioningInterfaces');
      }
    }

    adaptedTemplates[template.key] = template;
  });

  return adaptedTemplates;
}

function setOptionsForNetworkFields(
  template: FormTemplate,
  networks: Network[],
  xcipioNodes: NodeData[],
  networkFields: string[] = ['cdnetid', 'netid', 'ownnetid', 'ccnetid']) {
  const nodeNameToNodeTypeMap = xcipioNodes.reduce((acc, curr) => {
    acc[curr.nodeName] = curr.nodeType;
    return acc;
  }, {} as { [key: string]: string });
  const x2Networks = networks.filter((n) => !n.nodeName || nodeNameToNodeTypeMap[n.nodeName] === 'MDF2' || nodeNameToNodeTypeMap[n.nodeName] === 'XCP');
  const x3Networks = networks.filter((n) => !n.nodeName || nodeNameToNodeTypeMap[n.nodeName] === 'MDF3' || nodeNameToNodeTypeMap[n.nodeName] === 'XCP');
  const otherNetworks = networks.filter((n) => !n.nodeName || (nodeNameToNodeTypeMap[n.nodeName] !== 'MDF3' && nodeNameToNodeTypeMap[n.nodeName] !== 'MDF2'));

  Object.keys(template).forEach((section: string) => {
    const templateSection = template[section];

    if (typeof templateSection === 'string') {
      return;
    }

    if (section.includes('provisioningInterfaces')) {
      networkFields.filter((f) => f in templateSection.fields).forEach((f) => {
        const nodeTypes = templateSection.fields[f].netidNodeTypes ?? undefined;
        const x1Networks = nodeTypes ? networks.filter((n) => !n.nodeName || nodeTypes.includes(nodeNameToNodeTypeMap[n.nodeName])) : networks;
        setOptionForTemplateSection(templateSection, getNetworkOptionsMap(x1Networks), [f]);
      });
    } else if (section.includes('dataInterfaces')) {
      setOptionForTemplateSection(templateSection, getNetworkOptionsMap(x2Networks), networkFields);
    } else if (section.includes('contentInterfaces')) {
      setOptionForTemplateSection(templateSection, getNetworkOptionsMap(x3Networks), networkFields);
    } else if (section.includes('monitoringCenters')) {
      setOptionForTemplateSection(templateSection, getNetworkOptionsMap(otherNetworks), networkFields);
    } else if (section.includes('usnbdAgencies')) {
      setOptionForTemplateSection(templateSection, getNetworkOptionsMap(otherNetworks), networkFields);
    } else if (section.includes('usnbdCallDataChannels')) {
      setOptionForTemplateSection(templateSection, getNetworkOptionsMap(otherNetworks), networkFields);
    }
  });
}

export function setOptionsForLiX1Fields(template: FormTemplate, accessFunctions: AccessFunction[], networks: Network[], ccpagOptions: FormTemplateOption[], xcf3Options: FormTemplateOption[], mdf2Options: FormTemplateOption[], mdf3Options: FormTemplateOption[]) {
  if (!['SS8_MDF2', 'SS8_MDF3', 'SS8_CCPAG', 'SS8_XCF3'].includes(template.key)) {
    return;
  }
  const serverAfs = accessFunctions.filter((af) => af.type.includes('_SVR'));
  if (template.key === 'SS8_MDF2') {
    const res = getNodeNameOptionsForMatchingNetworks(serverAfs, mdf2Options, networks);
    if (res.length <= 0) {
      (template.provisioningInterfaces as FormTemplateSection).fields.li_x1_mdf2nodename.hintMessage = 'No server (i.e. where Role = Server) has been created. Please create Server first before creating the client';
    } else {
      (template.provisioningInterfaces as FormTemplateSection).fields.li_x1_mdf2nodename.hintMessage = '';
    }
    setOptionsForFields(template, res, ['li_x1_mdf2nodename']);
  } else if (template.key === 'SS8_MDF3') {
    const res = getNodeNameOptionsForMatchingNetworks(serverAfs, mdf3Options, networks);
    if (res.length <= 0) {
      (template.provisioningInterfaces as FormTemplateSection).fields.li_x1_mdf3nodename.hintMessage = 'No server (i.e. where Role = Server) has been created. Please create Server first before creating the client';
    } else {
      (template.provisioningInterfaces as FormTemplateSection).fields.li_x1_mdf3nodename.hintMessage = '';
    }
    setOptionsForFields(template, res, ['li_x1_mdf3nodename']);
  } else if (template.key === 'SS8_CCPAG') {
    const res = getNodeNameOptionsForMatchingNetworks(serverAfs, ccpagOptions, networks);
    if (res.length <= 0) {
      (template.provisioningInterfaces as FormTemplateSection).fields.li_x1_ccpagnodename.hintMessage = 'No server (i.e. where Role = Server) has been created. Please create Server first before creating the client';
    } else {
      (template.provisioningInterfaces as FormTemplateSection).fields.li_x1_ccpagnodename.hintMessage = '';
    }
    setOptionsForFields(template, res, ['li_x1_ccpagnodename']);
  } else if (template.key === 'SS8_XCF3') {
    const res = getNodeNameOptionsForMatchingNetworks(serverAfs, xcf3Options, networks);
    if (res.length <= 0) {
      (template.provisioningInterfaces as FormTemplateSection).fields.li_x1_xcf3nodename.hintMessage = 'No server (i.e. where Role = Server) has been created. Please create Server first before creating the client';
    } else {
      (template.provisioningInterfaces as FormTemplateSection).fields.li_x1_xcf3nodename.hintMessage = '';
    }
    setOptionsForFields(template, res, ['li_x1_xcf3nodename']);
  }
}

function getNodeNameOptionsForMatchingNetworks(serverAfs: AccessFunction[], nodeOptions: FormTemplateOption[], networks: Network[]) {
  return nodeOptions.filter((mdf3: FormTemplateOption) => {
    const filteredNetworkIds = networks.filter((n) => n.nodeName === mdf3.label).map((n) => n.networkId);
    return serverAfs.some((serverAf) => {
      if (_.isArray(serverAf.provisioningInterfaces)) {
        const ownnetid = serverAf.provisioningInterfaces[0].options.find((o) => o.key === 'ownnetid');
        const decoded = Buffer.from(ownnetid?.value as string, 'base64').toString('binary') as string;
        return filteredNetworkIds.includes(decoded as string);
      }
      return false;
    });
  });
}

export function adaptTemplatesBasedOnAppSettings(
  templates: { [key: string]: FormTemplate },
  isLocationRequired?: boolean,
  isIdentifierAssociationExtensionsEnabled?: boolean,
): { [key: string]: FormTemplate } {
  const adaptedTemplates: { [key: string]: FormTemplate } = {};

  Object.keys(templates).forEach((templateKey) => {
    let template = cloneDeep(templates[templateKey]);

    if (isLocationRequired) {
      adaptLocationFields(template, isLocationRequired);
    }

    const contextProperties: FormTemplateVisibilityProperties | undefined = isIdentifierAssociationExtensionsEnabled !== undefined
      ? { isIdentifierAssociationExtensionsEnabled }
      : undefined;

    template = adaptTemplateFieldVisibility(template, {
      properties: contextProperties,
    });

    adaptedTemplates[template.key] = template;
  });

  return adaptedTemplates;
}

function adaptLocationFields(template: FormTemplate, isLocationRequired: boolean): void {
  const systemInformationFields = (template.systemInformation as FormTemplateSection).fields;

  if (systemInformationFields?.country?.validation?.required !== undefined) {
    systemInformationFields.country.validation.required = isLocationRequired;
  }

  if (systemInformationFields?.stateName?.validation?.required !== undefined) {
    systemInformationFields.stateName.validation.required = isLocationRequired;
  }

  if (systemInformationFields?.city?.validation?.required !== undefined) {
    systemInformationFields.city.validation.required = isLocationRequired;
  }
}

export function adaptTemplatePCRFFields(
  template: FormTemplate | undefined,
  mode: 'create' | 'edit' | 'view' | 'copy') {
  if (template == null) {
    return;
  }

  if (template.key !== 'PCRF') {
    return template;
  }

  const clonedTemplate = cloneDeep(template);

  const provisioningInterfaces = (clonedTemplate.provisioningInterfaces as FormTemplateSection);
  if (provisioningInterfaces != null) {
    if (mode === 'view') {
      clonedTemplate.provisioningInterfaces = adaptTemplateSection5GField(provisioningInterfaces, 'mdf3NodeName');
    }
  }

  return clonedTemplate;
}

export function adaptDeliveryViaField(
  template: FormTemplate | undefined,
  formData: AccessFunctionFormData
): FormTemplate | undefined {
  if (!template) {
    return;
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  const generalFields = (clonedTemplate.general as FormTemplateSection)?.fields;
  const dataInterfacesFields = (clonedTemplate.dataInterfaces as FormTemplateSection)?.fields;
  const dataDeliveryVia = formData.dataDeliveryVia ?? '';
  const contentInterfacesFields = (clonedTemplate.contentInterfaces as FormTemplateSection)?.fields;
  const contentDeliveryVia = formData.contentDeliveryVia ?? '';

  if (generalFields && contentDeliveryVia !== '') {
    if (generalFields.contentDeliveryVia) {
      generalFields.contentDeliveryVia.initial = contentDeliveryVia;
    }
  }

  if (dataInterfacesFields && dataDeliveryVia !== '') {
    if (dataInterfacesFields.dataDeliveryVia) {
      dataInterfacesFields.dataDeliveryVia.initial = dataDeliveryVia;
    }

    if (dataDeliveryVia !== 'ss8Distributor') {
      if (dataInterfacesFields.mdf2NodeName) {
        dataInterfacesFields.mdf2NodeName.hide = 'true';
      }
    }
  }

  if (contentInterfacesFields && contentDeliveryVia !== '') {
    if (contentInterfacesFields.contentDeliveryVia) {
      contentInterfacesFields.contentDeliveryVia.initial = contentDeliveryVia;
    }

    if (contentDeliveryVia !== 'ss8Distributor') {
      if (contentInterfacesFields.mdf3NodeName) {
        contentInterfacesFields.mdf3NodeName.hide = 'true';
      }
    }
  }

  return clonedTemplate;
}

export function adaptCcdestipField(
  template: FormTemplate | undefined,
  formData: AccessFunctionFormData,
  xcipioNodes: NodeData[],
): FormTemplate | undefined {
  if (!template) {
    return;
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  const formSubsectionName = getSubsectionName(formData, template, 'provisioningInterfaces');
  const provisioningInterfacesFields = (clonedTemplate[`provisioningInterfaces_${formSubsectionName}`] as FormTemplateSection)?.fields ?? (clonedTemplate.provisioningInterfaces as FormTemplateSection)?.fields;
  const provisioningInterfacesFromData = formData?.provisioningInterfaces ?? [];

  if (provisioningInterfacesFromData.length) {
    const ccdestip = provisioningInterfacesFromData[0].ccdestip ?? '::';
    const mdf3NodeName = provisioningInterfacesFromData[0].mdf3NodeName ?? '';

    if (provisioningInterfacesFields && provisioningInterfacesFields.ccdestip) {
      provisioningInterfacesFields.ccdestip.initial = ccdestip;

      const node = xcipioNodes?.find((node) => node.nodeName === mdf3NodeName);

      if (node) {
        const ipV4 = node.ipAddress;
        const ipV6 = node.ipAddress6;
        const newCcdestipOptions = [
          { label: ipV4, value: ipV4 },
          { label: ipV6, value: ipV6 }
        ]

        provisioningInterfacesFields.ccdestip.options = newCcdestipOptions;
        provisioningInterfacesFields.ccdestip.initial = ccdestip;
      }
    }
  }

  return clonedTemplate;
}

export function adaptTemplate5GFields(
  template: FormTemplate | undefined,
  is5GCVcpEnabled: boolean,
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const bypassAdaptationKeyList = ['PCRF', 'ERK_5GNEF']
  if (is5GCVcpEnabled || bypassAdaptationKeyList.includes(template.key)) {
    return template;
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  const provisioningInterfaces = (clonedTemplate.provisioningInterfaces as FormTemplateSection);
  if (provisioningInterfaces != null) {
    clonedTemplate.provisioningInterfaces = adaptTemplateSection5GField(provisioningInterfaces, 'ccnodename');
    clonedTemplate.provisioningInterfaces = adaptTemplateSection5GField(provisioningInterfaces, 'mdf2NodeName');
    clonedTemplate.provisioningInterfaces = adaptTemplateSection5GField(provisioningInterfaces, 'mdf3NodeName');
    clonedTemplate.provisioningInterfaces = adaptTemplateSection5GField(provisioningInterfaces, 'nodeNameType');
  }

  return clonedTemplate;
}

export function adaptTemplateFor5GPOIs(
  template: FormTemplate | undefined,
  fieldName: keyof AccessFunctionInterfaceFormData,
  fieldValue: string,
  is5GCVcpEnabled: boolean,
  enabledAccessFunctions: SupportedFeaturesAccessFunctions) {
  if (!is5GPOI(template, enabledAccessFunctions) || !is5GCVcpEnabled) {
    return template;
  }
  if (fieldName !== 'dsid_supported') {
    return template;
  }
  return template;
}
export function is5GPOI(template: FormTemplate | string | undefined, enabledAccessFunctions: SupportedFeaturesAccessFunctions): boolean {
  if (!template) {
    return false;
  }

  const templateKey = typeof template === 'string' ? template : template.key;

  const services = enabledAccessFunctions?.[templateKey]?.services;
  if (!services || templateKey === 'DRVIRTUAL') {
    return false;
  }

  if (!Array.isArray(services)) {
    // NOTE: some afs have a json object to represnet the services
    return Object.keys(services)?.includes('5G-DATA');
  }

  return services?.includes('5G-DATA');
}

function adaptTemplateSection5GField(formTemplateSection: FormTemplateSection, fieldName: string): FormTemplateSection {

  const field = formTemplateSection?.fields?.[fieldName];
  if (field == null) {
    return formTemplateSection;
  }

  field.hide = 'true';

  const { layout } = formTemplateSection.metadata;
  if (layout != null) {
    layout.rows = removeFieldFromLayoutRows(layout.rows, fieldName);
  }

  return formTemplateSection;
}

/**
 * This function is used to remove a field from the form template layout.
 * This handles the following cases:
 * - Add a '$empty' field at the end of the same row if that is tha last row in the layout
 * - Move a field from the next row to a previous row and add '$empty' to the last row
 * - Adjust the state field to be in required position depending on reqState
 *
 * NOTE: This method was made public so it is possible to add UT to it.
 * However it should not be used outside the context of this file.
 *
 * Please check the Unit Tests for documented scenarios.
 *
 * @param rows the row to remove the field
 * @param fieldToRemove the field to be removed
 * @returns the new rows with the field removed
 */
export function removeFieldFromLayoutRows(
  rows: string[][],
  fieldToRemove: string,
): string[][] {
  if (rows == null) {
    return rows;
  }

  // Assume the column size for the form is 4, but get the real number below
  let columnsSize = 4;
  // Get the row index for the field to be removed. Any previous row will
  // remain the same
  const rowIndex = rows.findIndex((row) => {
    const found = row.find((field) => field === fieldToRemove);

    if (found) {
      columnsSize = row.length;
    }

    return found;
  });

  if (rowIndex === -1) {
    // Nothing to be done, the field was not found
    return rows;
  }

  // Rows that will not be changed
  const unchangedRows = rows.slice(0, rowIndex);

  let remainingFields = rows.slice(rowIndex, rows.length).reduce((fields, row) => {
    fields.push(...row);
    return fields;
  }, []);

  // Remove the field to be removed
  remainingFields = remainingFields.filter((field) => field !== fieldToRemove);

  // Remove $empty - they will be added back few lines below
  remainingFields = remainingFields.filter((field) => field !== EMPTY_FIELD);

  // Check if there is state field, remove it to be added at the end
  const hasStateField = remainingFields.find((field) => field === ACCESS_FUNCTION_STATE_FIELD);
  if (hasStateField) {
    // Remove the state field for now
    remainingFields = remainingFields.filter((field) => field !== ACCESS_FUNCTION_STATE_FIELD);
  }

  const changedRows: string[][] = [];
  // Restore each row based in the columnsSize
  for (let i = 0; i <= remainingFields.length; i += columnsSize) {
    const newRow = [];
    for (let j = 0; j < columnsSize; j += 1) {
      newRow.push(remainingFields[i + j] ?? EMPTY_FIELD);
    }

    const isRowEmpty = newRow.every((field) => field === EMPTY_FIELD);
    if (!isRowEmpty) {
      changedRows.push(newRow);
    }
  }

  if (hasStateField) {
    // Restore the state field back
    let reqStatePosition = remainingFields.findIndex((field) => field === ACCESS_FUNCTION_REQ_STATE_FIELD);
    reqStatePosition %= columnsSize;
    const newRow = Array(columnsSize).fill(EMPTY_FIELD);
    newRow[reqStatePosition] = ACCESS_FUNCTION_STATE_FIELD;
    changedRows.push(newRow);
  }

  return [...unchangedRows, ...changedRows];
}

export function adaptTemplateVisibilityMode(
  template: FormTemplate | undefined,
  mode: 'create' | 'edit' | 'view' | 'copy',
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);

  if (mode === 'copy') {
    adaptTemplateForCopyMode(clonedTemplate);
    adaptTemplateFileImportExport(clonedTemplate);
    adaptTemplateUsnbdAgencies(clonedTemplate);
    adaptTemplateUsnbdCallDataChannels(clonedTemplate, mode);

  } else if (mode === 'edit') {
    adaptTemplateForEditMode(clonedTemplate);
    adaptTemplateUsnbdAgencies(clonedTemplate);
    adaptTemplateUsnbdCallDataChannels(clonedTemplate, mode);

  } else if (mode === 'view') {
    adaptTemplateForViewMode(clonedTemplate);

  } else if (mode === 'create') {
    adaptTemplateFileImportExport(clonedTemplate);
    adaptTemplateUsnbdAgencies(clonedTemplate);
    adaptTemplateUsnbdCallDataChannels(clonedTemplate, mode);
  }

  return clonedTemplate;
}

function adaptTemplateFileImportExport(clonedTemplate: FormTemplate) {
  if (clonedTemplate.key === 'CBS') {
    attemptToMakeFieldDisabledFromSection(clonedTemplate, 'fileImportExport', 'fileLocation');
  }
}

function adaptTemplateUsnbdAgencies(clonedTemplate: FormTemplate) {
  attemptToMakeFieldHiddenFromSection(clonedTemplate, 'usnbdAgencies', 'status');
}

function adaptTemplateUsnbdCallDataChannels(clonedTemplate: FormTemplate, mode: 'create' | 'edit' | 'view' | 'copy') {
  attemptToMakeFieldHiddenFromSection(clonedTemplate, 'usnbdCallDataChannels', 'cdcindex');
  attemptToMakeFieldHiddenFromSection(clonedTemplate, 'usnbdCallDataChannels', 'cdcstatus');

  if (mode === 'edit') {
    attemptToMakeFieldDisabledFromSection(clonedTemplate, 'usnbdAgencies', 'name');
    attemptToMakeFieldDisabledFromSection(clonedTemplate, 'usnbdAgencies', 'sts');
    attemptToMakeFieldDisabledFromSection(clonedTemplate, 'usnbdAgencies', 'pretranslator');
    attemptToMakeFieldDisabledFromSection(clonedTemplate, 'usnbdAgencies', 'lca');
    attemptToMakeFieldDisabledFromSection(clonedTemplate, 'usnbdAgencies', 'billno');
    attemptToMakeFieldDisabledFromSection(clonedTemplate, 'usnbdAgencies', 'pic');
    attemptToMakeFieldDisabledFromSection(clonedTemplate, 'usnbdAgencies', 'lata');
  }
}

export function adaptLocationField(
  template: FormTemplate | undefined,
  list: string[],
  field: 'stateName' | 'city',
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  const options: FormTemplateOption[] = getFieldOptions(list);
  setLocationFieldOptions(clonedTemplate, options, field);

  return clonedTemplate;
}

export function adaptTimezoneField(
  template: FormTemplate | undefined,
  timezones: ValueLabelPair[],
  accessFunction: AccessFunctionFormData,
): FormTemplate | undefined {
  if (template == null) {
    return template;
  }

  const accessFunctionTimezoneExists = timezones.some(({ value }) => value === accessFunction.timeZone);
  if (accessFunctionTimezoneExists) {
    return template;
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  const newTimezones = [...timezones, { value: accessFunction.timeZone, label: accessFunction.timeZone }];
  const timezoneOptions = getTimeZoneFieldOptions(newTimezones);
  resetTimezoneOptions(clonedTemplate);
  setTimezoneOptions(clonedTemplate, timezoneOptions);

  return clonedTemplate;
}

export function adaptSecurityInfoField(
  template: FormTemplate | undefined,
  securityInfos: SecurityInfo[],
  securityInfoFilterByInterface: SecurityInfoFilterByInterface,
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const ss8Templates = ['SS8_CCPAG', 'SS8_MDF2', 'SS8_MDF3'];
  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  Object.entries(clonedTemplate).forEach(([sectionName, templateSection]) => {
    if (typeof templateSection === 'string') {
      return;
    }

    cleanupPreviousSecurityInfoOption(templateSection, 'secinfoid');

    const securityInfoFilter = securityInfoFilterByInterface[sectionName] ?? [];
    const securityInfoOptions = getSecurityInfoOptions(securityInfos, securityInfoFilter);

    setOptionForTemplateSection(templateSection, securityInfoOptions, ['secinfoid']);
    if (ss8Templates.includes(template.key)) {
      const { fields } = templateSection;
      const found = securityInfoOptions.find((x) => x.label === 'SS8IntClient13');
      if (found && fields.secinfoid) {
        fields.secinfoid.initial = found.value;
      }
    }
  });

  return clonedTemplate;
}

export function adaptNetworkIdField(
  templateSection: FormTemplateSection,
  interfaceFormData: Partial<AccessFunctionInterfaceFormData>,
): FormTemplateSection {

  let adaptedTemplate = templateSection;

  if (interfaceFormData.ownnetid != null) {
    adaptedTemplate = adaptNetworkIdFieldByFieldName(
      adaptedTemplate,
      interfaceFormData.ownnetid,
      'ownnetid',
    );
  }

  if (interfaceFormData.cdnetid != null) {
    adaptedTemplate = adaptNetworkIdFieldByFieldName(
      adaptedTemplate,
      interfaceFormData.cdnetid,
      'cdnetid',
    );
  }

  return adaptedTemplate;
}

export function adaptXDPIpAddress(
  templateSection: FormTemplateSection,
  interfaceFormData: Partial<AccessFunctionInterfaceFormData>,
): FormTemplateSection {
  const formTemplateField = templateSection.fields.ownIPAddr;

  if (formTemplateField == null || formTemplateField.type !== 'select') {
    return templateSection;
  }

  const fieldValue = interfaceFormData.ownIPAddr;
  const isOptionAvailable = formTemplateField.options?.some(({ value }) => value === fieldValue);
  if (fieldValue == null || fieldValue === '' || isOptionAvailable) {
    return templateSection;
  }

  const clonedTemplateSection = cloneDeep(templateSection);

  clonedTemplateSection.fields.ownIPAddr.options?.push({
    label: fieldValue,
    value: fieldValue,
  });

  return clonedTemplateSection;
}

export function adaptTemplateLabelsForDeployment(
  template: FormTemplate | undefined,
  deployment: Deployment,
): FormTemplate | undefined {
  if (template == null) {
    return template;
  }

  const clonedTemplate = cloneDeep(template);

  if (deployment === 'endeavor') {
    clonedTemplate.formId = Date.now().toString();
    setLabelForField(clonedTemplate, 'stateName', 'County/Region');
    setLabelForField(clonedTemplate, 'city', 'Town/City');
  }

  return clonedTemplate;
}

function setLabelForField(template: FormTemplate, fieldName: string, fieldLabel: string) {
  Object.keys(template).forEach((section: string) => {
    const templateSection = template[section];

    if (typeof templateSection === 'string') {
      return;
    }

    const { fields } = templateSection;
    if (fields == null || fields[fieldName] == null) {
      return;
    }

    const templateField = fields[fieldName];
    const originalLabel = templateField.label;
    templateField.label = fieldLabel;

    if (originalLabel != null && templateField.options != null) {
      const { options } = templateField;
      options[0].label = options[0].label.replace(new RegExp(originalLabel, 'i'), fieldLabel);
    }
  });
}

export function adaptTemplateInterfaceFields(
  template: FormTemplate | undefined,
  formData: AccessFunctionFormData,
  xcipioNodes?: NodeData[],
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);
  const provisioningInterfacesSubsectionName = getSubsectionName(formData, template, 'provisioningInterfaces');
  const dataInterfacesSubsectionName = getSubsectionName(formData, template, 'dataInterfaces');
  const contentInterfacesSubsectionName = getSubsectionName(formData, template, 'contentInterfaces');

  formData.provisioningInterfaces?.forEach((interfaceFormData) => {
    if (interfaceFormData.uri != null) {
      const templateSection = getInterfaceFormTemplateSection(clonedTemplate, 'provisioningInterfaces', provisioningInterfacesSubsectionName);
      const templateField = templateSection?.fields.uri;

      if (fieldAffectsOtherFieldsOnChange(templateField)) {
        adaptTemplateInterfaceForUri(
          templateField?.affectedFieldsOnChange,
          templateSection,
          interfaceFormData.uri,
          template.key,
        );
      }
    }

    if (interfaceFormData.ccnodename != null) {
      const templateSection = getInterfaceFormTemplateSection(clonedTemplate, 'provisioningInterfaces', provisioningInterfacesSubsectionName);
      const contentInterfacesTemplateSection = getInterfaceFormTemplateSection(clonedTemplate, 'contentInterfaces', contentInterfacesSubsectionName);
      const ccnodenameField = templateSection?.fields.ccnodename;
      const mdf3NodeNameField = templateSection?.fields.mdf3NodeName;
      const nodeNameTypeField = templateSection?.fields.nodeNameType;
      const moduleNameField = contentInterfacesTemplateSection?.fields.moduleName;
      const contentInterfacesMetadata = contentInterfacesTemplateSection?.metadata;

      if (ccnodenameField && mdf3NodeNameField && nodeNameTypeField) {
        adaptNodeNamesForSMF(
          formData.tag,
          ccnodenameField,
          mdf3NodeNameField,
          nodeNameTypeField,
          moduleNameField as FormTemplateField,
          contentInterfacesMetadata as FormTemplateMetaData,
          templateSection,
          interfaceFormData.ccnodename,
          xcipioNodes,
        );
        template.formId = Date.now().toString();
      }
    }
  });

  formData.dataInterfaces?.forEach((interfaceFormData) => {
    const templateSection = getInterfaceFormTemplateSection(clonedTemplate, 'dataInterfaces', dataInterfacesSubsectionName);
    if (interfaceFormData.uri != null) {
      const templateField = templateSection?.fields.tpkt;
      if (fieldAffectsOtherFieldsOnChange(templateField)) {
        adaptTemplateForTPKT(
          templateField?.affectedFieldsOnChange,
          templateSection,
          interfaceFormData.tpkt,
        );
        adaptTemplateForFtpType(
          templateField?.affectedFieldsOnChange,
          templateSection,
          interfaceFormData.ftpType,
        );
      }
    }

    if (interfaceFormData.deliveryVia != null) {
      const templateField = templateSection?.fields.deliveryVia;
      if (fieldAffectsOtherFieldsOnChange(templateField)) {
        adaptTemplateInterfaceForDeliveryVia(
          templateSection,
          interfaceFormData.deliveryVia,
        );
      }
    }
  });

  formData.contentInterfaces?.forEach((interfaceFormData) => {
    if (interfaceFormData.maxx2connsperaf != null) {
      const templateSection = getInterfaceFormTemplateSection(clonedTemplate, 'contentInterfaces', contentInterfacesSubsectionName);
      const templateField = templateSection?.fields.maxx2connsperaf;

      if (fieldAffectsOtherFieldsOnChange(templateField)) {
        adaptTemplateInterfaceForMaxx2Conn(
          templateField?.affectedFieldsOnChange,
          templateSection,
          interfaceFormData.maxx2connsperaf,
        );
      }
    }
  });

  return clonedTemplate;
}

export function getSubsectionName(
  data: Partial<AccessFunctionFormData>,
  template: FormTemplate,
  interfaceName: AccessFunctionInterfaceFormSectionType,
): string {
  let formSubsectionName = data.contentDeliveryVia ?? '';
  if (isHeaderSelectionPanel(template, interfaceName)) {
    if (interfaceName === 'dataInterfaces') {
      formSubsectionName = data.dataDeliveryVia ?? '';
    } else if (interfaceName === 'contentInterfaces') {
      formSubsectionName = data.contentDeliveryVia ?? '';
    } else {
      formSubsectionName = '';
    }
  }
  return formSubsectionName;
}

export function adaptModelServiceField(
  template: FormTemplate | undefined,
  contentDeliveryVia: string,
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  (clonedTemplate.general as FormTemplateSection).fields.contentDeliveryVia.initial = contentDeliveryVia;

  return clonedTemplate;
}

export function adaptTemplateInterfaceForCiscoErModel(
  template: FormTemplate | undefined,
  fieldName: keyof AccessFunctionInterfaceFormData,
  fieldValue: string,
): FormTemplate | undefined {

  if (template == null) {
    return;
  }

  if (fieldName !== 'model' || template?.key !== 'CISCO_ER') {
    return cloneDeep(template);
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  const {
    typeofservice,
    typeofservicemask,
    vpnrouteforward,
  } = (clonedTemplate.provisioningInterfaces_directFromAF as FormTemplateSection).fields;

  if (fieldValue === 'CISCO_CRS') {
    typeofservice.hide = 'false';
    typeofservicemask.hide = 'false';
    vpnrouteforward.hide = 'false';
  } else {
    typeofservice.hide = 'true';
    typeofservicemask.hide = 'true';
    vpnrouteforward.hide = 'true';
  }

  return clonedTemplate;
}

export function adaptTemplateInterfaceForUtimacoModel(
  template: FormTemplate | undefined,
  fieldName: keyof AccessFunctionInterfaceFormData,
  fieldValue: string,
): FormTemplate | undefined {
  if (template == null || fieldName !== 'deliveryFormat' || template?.key !== 'SIEMENS_LIMS') {
    return;
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  const {
    naming,
    networkPacket,
  } = (clonedTemplate.dataInterfaces_directFromAF as FormTemplateSection).fields;

  if (fieldValue) {
    naming.hide = 'true';
    networkPacket.hide = 'true';
  } else {
    naming.hide = 'false';
    networkPacket.hide = 'false';
  }

  return clonedTemplate;
}

export function adaptTemplateForInterfacesFieldUpdate(
  interfaceName: AccessFunctionInterfaceFormSectionType,
  data: Partial<AccessFunctionFormData>,
  template: FormTemplate | undefined,
  fieldName: keyof AccessFunctionInterfaceFormData,
  fieldValue: unknown,
  xcipioNodes?: NodeData[],
  networks?: Network[],
  accessFunctions?: AccessFunction[],
  templates?: { [key: string]: FormTemplate; } | undefined
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);
  const formSubsectionName = getSubsectionName(data, template, interfaceName);

  const templateSection = getInterfaceFormTemplateSection(clonedTemplate, interfaceName, formSubsectionName);
  const templateField = templateSection?.fields[fieldName];

  if (fieldName === 'ccnodename') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      clonedTemplate.formId = Date.now().toString();
    }
  }

  if (fieldName === 'mdf3NodeName') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      clonedTemplate.formId = Date.now().toString();

      if (templateField?.affectedFieldsOnChange?.includes('ccdestip')) {
        const node = xcipioNodes?.find((node) => node.nodeName === fieldValue);

        if (node) {
          const ipV4 = node.ipAddress;
          const ipV6 = node.ipAddress6;

          const sectionName = formSubsectionName === '' ? interfaceName : `${interfaceName}_${formSubsectionName}`;
          const provisioningInterfacesFields = (clonedTemplate[sectionName] as FormTemplateSection)?.fields;

          if (provisioningInterfacesFields && provisioningInterfacesFields.ccdestip) {
            const newCcdestipOptions = [
              { label: ipV4, value: ipV4 },
              { label: ipV6, value: ipV6 }
            ]

            provisioningInterfacesFields.ccdestip.options = newCcdestipOptions;
            provisioningInterfacesFields.ccdestip.initial = ipV4;
          }
        }
      }
    }
  }

  if (fieldName === 'uri') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      clonedTemplate.formId = Date.now().toString();

      adaptTemplateInterfaceForUri(
        templateField?.affectedFieldsOnChange,
        templateSection,
        fieldValue as string,
        template.key,
      );
    }
  }

  if (fieldName === 'deliveryFormat') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      clonedTemplate.formId = Date.now().toString();

      adaptTemplateInterfaceForDeliveryFormat(
        templateField?.affectedFieldsOnChange,
        templateSection,
        fieldValue as string,
        template.key,
      );
    }
  }

  if (fieldName === 'securitylvl') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      clonedTemplate.formId = Date.now().toString();

      adaptTemplateInterfaceForSecuritylvl(
        templateField?.affectedFieldsOnChange,
        templateSection,
        fieldValue as string,
        template.key,
      );
    }
  }

  if (fieldName === 'maxx2connsperaf') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      clonedTemplate.formId = Date.now().toString();

      adaptTemplateInterfaceForMaxx2Conn(
        templateField?.affectedFieldsOnChange,
        templateSection,
        fieldValue as string,
      );
    }
  }

  if (fieldName === 'tpkt') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      clonedTemplate.formId = Date.now().toString();

      adaptTemplateForTPKT(
        templateField?.affectedFieldsOnChange,
        templateSection,
        fieldValue as boolean,
      );
    }
  }

  if (fieldName === 'deliveryVia') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      clonedTemplate.formId = Date.now().toString();

      adaptTemplateInterfaceForDeliveryVia(
        templateSection,
        fieldValue as string,
      );
    }
  }

  if (fieldName === 'ftpType') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      clonedTemplate.formId = Date.now().toString();

      adaptTemplateForFtpType(
        templateField?.affectedFieldsOnChange,
        templateSection,
        fieldValue as string,
      );
    }
  }

  if (fieldName === 'secinfoid') {
    clonedTemplate.formId = Date.now().toString();

    adaptTemplateForSecinfoid(
      clonedTemplate,
      templateSection,
      fieldValue as string,
    );
  }

  if (fieldName === 'deliveryFormat') {
    // clonedTemplate.formId = Date.now().toString();
    if (template.key === 'ERICSSON_LIMS') {
      if (fieldValue === 'TS201671') {
        // (clonedTemplate.contentInterfaces as FormTemplateSection).metadata.hide = 'true';
      } else {
        // (clonedTemplate.contentInterfaces as FormTemplateSection).metadata.hide = 'false';
      }
    }

    // if (fieldAffectsOtherFieldsOnChange(templateField)) {
    //   adaptTemplateInterfaceForDeliveryFormat(
    //     templateField?.affectedFieldsOnChange,
    //     templateSection,
    //     fieldValue as string,
    //     template.key,
    //   );
    // }

    // adaptTemplateForAdvancedSettingsVisibility(
    //   clonedTemplate,
    //   templateSection,
    //   fieldValue as string,
    // );
  }

  if (fieldName === 'netype' && template.key === 'ERICSSON_NE') {
    adaptTemplateForNetTypeAndResVersion(clonedTemplate, fieldName, fieldValue as string);
    clonedTemplate.formId = Date.now().toString();
  }

  if (fieldName === 'interface') {
    clonedTemplate.formId = Date.now().toString();
  }

  if (fieldName === 'nodeNameType') {
    if (fieldAffectsOtherFieldsOnChange(templateField)) {
      clonedTemplate.formId = Date.now().toString();
      adaptTemplateForNodeNameType(clonedTemplate, fieldName, fieldValue as string, data, xcipioNodes, templates);
    }
  }

  if (template.key === 'COGNYTE') {
    if (fieldName === 'cdtype') {
      if (fieldValue === 'FTP') {
        (clonedTemplate.monitoringCenters as FormTemplateSection).fields.cdport.initial = '21';
        (clonedTemplate.monitoringCenters as FormTemplateSection).fields.cdport.disabled = true;
      } else {
        (clonedTemplate.monitoringCenters as FormTemplateSection).fields.cdport.initial = '';
        (clonedTemplate.monitoringCenters as FormTemplateSection).fields.cdport.disabled = false;
      }
      const hasFtpMonitoringCenter = !!data.monitoringCenters
        && data.monitoringCenters.some((monitoringCenter) => monitoringCenter.cdtype === 'FTP');

      (clonedTemplate.dataInterfaces as FormTemplateSection).metadata.min = hasFtpMonitoringCenter ? 1 : 0;
    }
    if (fieldName === 'cctype') {
      if (fieldValue === 'VOIP') {
        (clonedTemplate.monitoringCenters as FormTemplateSection).fields.ccdest.placeholder = 'e.g. 10.0.0.1#443';
      } else {
        (clonedTemplate.monitoringCenters as FormTemplateSection).fields.ccdest.placeholder = '';
      }
    }
  }

  adaptTemplateAndDataFor5GAfsForGROnFieldUpdate(clonedTemplate, fieldName, fieldValue as boolean, data, xcipioNodes, networks, accessFunctions, templates);
  adaptTemplateFor4GAfsForGROnFieldUpdate(clonedTemplate, fieldName, fieldValue as string, data, xcipioNodes, networks, templates);
  return clonedTemplate;
}

export function adaptTemplateDeliveryViaField(
  template: FormTemplate | undefined,
  formSection: string,
  data: Partial<AccessFunctionFormData>,
  fieldName: string,
  value: string,
  enabledFeatures?: string[],
): FormTemplate | undefined {
  if (!template) {
    return template;
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  const generalFields = (clonedTemplate.general as FormTemplateSection)?.fields;
  const dataInterfacesFields = (clonedTemplate.dataInterfaces as FormTemplateSection)?.fields;
  const contentInterfacesFields = (clonedTemplate.contentInterfaces as FormTemplateSection)?.fields;

  if (formSection === 'general' && fieldName === 'contentDeliveryVia') {
    if (generalFields) {
      if (generalFields.contentDeliveryVia) {
        generalFields.contentDeliveryVia.initial = value;
      }
    }

    if (contentInterfacesFields) {
      if (contentInterfacesFields.contentDeliveryVia && contentInterfacesFields.mdf3NodeName) {
        contentInterfacesFields.contentDeliveryVia.initial = value;

        if (value === 'trafficManager') {
          if (contentInterfacesFields.mdf3NodeNameMultiSelect) {
            contentInterfacesFields.mdf3NodeNameMultiSelect.hide = 'true';
            contentInterfacesFields.mdf3NodeNameMultiSelect.validation = { required: false };
          }
          if (contentInterfacesFields.mdf3NodeName) {
            contentInterfacesFields.mdf3NodeName.hide = 'true';
            contentInterfacesFields.mdf3NodeName.validation = { required: false };
          }
        }
        if (value === 'ss8Distributor') {
          contentInterfacesFields.contentDeliveryVia.initial = 'ss8Distributor';
          if (contentInterfacesFields.mdf3NodeNameMultiSelect) {
            contentInterfacesFields.mdf3NodeNameMultiSelect.hide = 'false';
            contentInterfacesFields.mdf3NodeNameMultiSelect.validation = { required: true };
          } else {
            contentInterfacesFields.mdf3NodeName.hide = 'false';
            contentInterfacesFields.mdf3NodeName.validation = { required: true };
          }
        }
      }
    }

  } else if (formSection === 'dataInterfaces' && fieldName === 'dataDeliveryVia') {
    if (dataInterfacesFields) {
      if (dataInterfacesFields.dataDeliveryVia) {
        dataInterfacesFields.dataDeliveryVia.initial = value;

        if (value === 'ss8Distributor') {
          if (dataInterfacesFields.mdf2NodeNameMultiSelect) {
            dataInterfacesFields.mdf2NodeNameMultiSelect.hide = 'false';
            dataInterfacesFields.mdf2NodeNameMultiSelect.validation = { required: true };
          } else {
            dataInterfacesFields.mdf2NodeName.hide = 'false';
            dataInterfacesFields.mdf2NodeName.validation = { required: true };
          }
        } else if (value === 'directFromAF') {
          if (dataInterfacesFields.mdf2NodeNameMultiSelect) {
            dataInterfacesFields.mdf2NodeNameMultiSelect.hide = 'true';
            dataInterfacesFields.mdf2NodeNameMultiSelect.validation = { required: false };
          }
          if (dataInterfacesFields.mdf2NodeName) {
            dataInterfacesFields.mdf2NodeName.hide = 'true';
            dataInterfacesFields.mdf2NodeName.validation = { required: false };
          }
        }
      }
    }

  } else if (formSection === 'contentInterfaces' && fieldName === 'contentDeliveryVia') {
    if (contentInterfacesFields) {
      if (contentInterfacesFields.contentDeliveryVia) {
        contentInterfacesFields.contentDeliveryVia.initial = value;

        if (value === 'ss8Distributor') {
          if (contentInterfacesFields.mdf3NodeNameMultiSelect) {
            contentInterfacesFields.mdf3NodeNameMultiSelect.hide = 'false';
            contentInterfacesFields.mdf3NodeNameMultiSelect.validation = { required: true };
          } else {
            contentInterfacesFields.mdf3NodeName.hide = 'false';
            contentInterfacesFields.mdf3NodeName.validation = { required: true };
          }

          if (enabledFeatures) {
            const isXcCnfvcpEnabled = enabledFeatures.includes('XC-CNFVCP');
            if (!isXcCnfvcpEnabled && template.key === 'ACME_X123') {
              contentInterfacesFields.mdf3NodeName.hide = 'true';
              contentInterfacesFields.mdf3NodeName.validation = { required: false };
            }
          }
        } else if (value === 'directFromAF') {
          if (contentInterfacesFields.mdf3NodeName) {
            contentInterfacesFields.mdf3NodeName.hide = 'true';
            contentInterfacesFields.mdf3NodeName.validation = { required: false };
          }
          if (contentInterfacesFields.mdf3NodeNameMultiSelect) {
            contentInterfacesFields.mdf3NodeNameMultiSelect.hide = 'true';
            contentInterfacesFields.mdf3NodeNameMultiSelect.validation = { required: false };
          }
        }
      }
    }
  }

  return clonedTemplate;
}

export function getInterfaceFormTemplateSection(
  template: FormTemplate,
  interfaceName: AccessFunctionInterfaceFormSectionType,
  interfaceConfigureType: string,
): FormTemplateSection | undefined {
  const sectionName = getInterfaceFormTemplateSectionName(template, interfaceName, interfaceConfigureType);
  return template[sectionName] as FormTemplateSection;
}

export function getInterfaceFormTemplateSectionName(
  template: FormTemplate,
  interfaceName: AccessFunctionInterfaceFormSectionType,
  interfaceConfigureType: string,
): string {
  if (interfaceConfigureType === '') {
    return `${interfaceName}`;
  }

  if (template?.[`${interfaceName}_${interfaceConfigureType}`] == null) {
    return `${interfaceName}`;
  }

  return `${interfaceName}_${interfaceConfigureType}`;
}

export function shouldValidateInterfacesQuantity(
  template: FormTemplate,
  provisioningInterfacesSubsectionName: string,
  dataInterfacesSubsectionName: string,
  contentInterfacesSubsectionName: string,
  afOptionsSubsectionName: string,
  eimsNetworkElementSubsectionName: string,
): boolean {
  if (accessFunctionsAllowedWithoutInterfaces[template.key] === true) {
    return false;
  }

  const provisioningInterfacesTemplateSection = getInterfaceFormTemplateSection(template, 'provisioningInterfaces', provisioningInterfacesSubsectionName);
  const dataInterfacesTemplateSection = getInterfaceFormTemplateSection(template, 'dataInterfaces', dataInterfacesSubsectionName);
  const contentInterfacesTemplateSection = getInterfaceFormTemplateSection(template, 'contentInterfaces', contentInterfacesSubsectionName);
  const afOptionsTemplateSection = getInterfaceFormTemplateSection(template, 'afOptions', afOptionsSubsectionName);
  const eimsNetworkElementTemplateSection = getInterfaceFormTemplateSection(template, 'eimsNetworkElement', eimsNetworkElementSubsectionName);

  const shouldValidateEmptyInterfaces = [
    provisioningInterfacesTemplateSection,
    dataInterfacesTemplateSection,
    contentInterfacesTemplateSection,
    afOptionsTemplateSection,
    eimsNetworkElementTemplateSection,
  ]
    .filter((entry: FormTemplateSection | undefined) => entry != null)
    .map((entry: FormTemplateSection | undefined) => entry?.metadata.min === 0)
    .every((entry: boolean) => entry === true);

  return shouldValidateEmptyInterfaces;

}

export function getTagOptions(
  templates: { [key: string]: FormTemplate },
  enabledAccessFunctions: SupportedFeaturesAccessFunctions,
): FormTemplateOption[] {
  return Object.keys(templates).map((key) => ({
    label: enabledAccessFunctions[key]?.title ?? templates[key].name,
    value: templates[key].key,
  }))
    .sort((a, b) => a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase()));
}

function setInitialValueFromProcessOptions(template: FormTemplate, processOptions: ProcessOption[]): void {
  const { processOptionProcName } = template;

  if (processOptionProcName == null || typeof processOptionProcName !== 'string') {
    return;
  }

  const filteredProcessOptions = processOptions.filter(({ PROCNAME }) => PROCNAME === processOptionProcName);

  Object.values(template).forEach((formTemplateSection) => {
    if (typeof formTemplateSection === 'string') {
      return;
    }

    const { fields } = formTemplateSection;
    if (fields == null) {
      return;
    }

    Object.values(fields).forEach((formTemplateField) => {
      const {
        processOptionParamName,
        processOptionLoadParamValueTo,
      } = formTemplateField;

      if (processOptionParamName == null) {
        return;
      }

      const processOption = filteredProcessOptions.find(({ PARAMNAME }) => PARAMNAME === processOptionParamName);
      if (processOption == null || processOption.PARAMVALUE == null) {
        return;
      }

      if (processOptionLoadParamValueTo != null) {
        formTemplateField[processOptionLoadParamValueTo] = processOption.PARAMVALUE;

      } else {
        formTemplateField.initial = processOption.PARAMVALUE;
      }
    });
  });
}

function setTagOptions(template: FormTemplate, options: FormTemplateOption[]): void {
  (template.general as FormTemplateSection).fields.tag?.options?.push(...options);
}

function getFieldOptions(list: string[]): FormTemplateOption[] {
  return list.map((entry) => ({
    label: entry,
    value: entry,
  }));
}

function getTimeZoneFieldOptions(timezones: ValueLabelPair[]): FormTemplateOption[] {
  const date = new Date();
  return getSortedTimeZoneValueLabels(date, timezones);
}

function setLocationFieldOptions(
  template: FormTemplate,
  options: FormTemplateOption[],
  field: 'country' | 'stateName' | 'city',
): void {
  const templateSection = (template.systemInformation as FormTemplateSection);

  if (templateSection.fields[field] == null) {
    return;
  }

  const firstOption = templateSection.fields[field].options?.[0];
  if (firstOption) {
    templateSection.fields[field].options = [firstOption];
  }

  if (options.length > 0) {
    templateSection.fields[field].options?.push(...options);
    templateSection.fields[field].disabled = false;
  } else {
    templateSection.fields[field].disabled = true;
  }
}

function resetTimezoneOptions(template: FormTemplate): void {
  const { options } = (template.systemInformation as FormTemplateSection).fields.timeZone;

  if (options != null && options.length > 0) {
    (template.systemInformation as FormTemplateSection).fields.timeZone.options = [options[0]];
  }
}

function setTimezoneOptions(template: FormTemplate, options: FormTemplateOption[]): void {
  if (options.length > 0) {
    (template.systemInformation as FormTemplateSection).fields.timeZone.type = 'select';
    (template.systemInformation as FormTemplateSection).fields.timeZone.options?.push(...options);

    if (options.length === 1) {
      (template.systemInformation as FormTemplateSection).fields.timeZone.initial = options[0].value;
    }
  }
}

function getContactsOptions(contacts: Contact[]): FormTemplateOption[] {
  return contacts.map((contact) => ({
    label: contact.name,
    value: contact.id,
  }));
}

function getCspOptions(cspConfigs: CspConfig[]): FormTemplateOption[] {
  return cspConfigs.map(({ name, cspid }) => ({
    label: name,
    value: cspid,
  }));
}

function setCspDefaultOption(templateSection: FormTemplateSection): void {
  const cspField = templateSection?.fields?.cspid;

  if (cspField?.options == null) {
    return;
  }

  cspField.options[0] = { ...CSP_DEFAULT_OPTION };
}

function setContactOptions(template: FormTemplate, options: FormTemplateOption[]): void {
  (template.systemInformation as FormTemplateSection).fields.contact?.options?.push(...options);
}

export function getNetworkOptionsMap(networks: Network[]): FormTemplateOptionMap {
  return {
    default: getNetworkOptions(networks, 'default'),
    networkIdAndIpAddress: getNetworkOptions(networks, 'networkIdAndIpAddress'),
    networkIdIPAddressAndCustomPort: getNetworkOptions(networks, 'networkIdIPAddressAndCustomPort'),
    networkIdIPAddressAndPortIncrement: getNetworkOptions(networks, 'networkIdIPAddressAndPortIncrement')
  };
}

function getNetworkOptions(
  networks: Network[],
  type: 'networkIdAndIpAddress' | 'networkIdIPAddressAndCustomPort' | 'networkIdIPAddressAndPortIncrement' | 'default',
): FormTemplateOption[] {
  return networks.map((network) => {
    const name = network.networkId;

    let ip = network.ownIPAddress;
    if (ipRegex.v6({ exact: true }).test(ip)) {
      ip = `[${ip}]`;
    }

    const port = network.startPortNumber;
    const { nodeName } = network;

    let label = '';
    switch (type) {
      case 'networkIdAndIpAddress':
        label = (nodeName) ? `${name} - ${ip} - ${nodeName}` : `${name} - ${ip}`;
        break;
      case 'networkIdIPAddressAndCustomPort':
        label = (nodeName) ? `${name} - ${ip}:{{value}} - ${nodeName}` : `${name} - ${ip}:{{value}}`;
        break;
      case 'networkIdIPAddressAndPortIncrement':
        label = (nodeName) ? `${name} - ${ip}:<% print(+${port} + (+value)); %> - ${nodeName}` : `${name} - ${ip}:<% print(+${port} + (+value)); %>`;
        break;
      default:
        label = (nodeName) ? `${name} - ${ip}:${port} - ${nodeName}` : `${name} - ${ip}:${port}`;
    }

    const value = network.id;

    return {
      label,
      value,
    };
  });
}

function getSecurityInfoOptions(
  securityInfos: SecurityInfo[],
  securityInfoFilter: SecurityInfoFilter[],
): FormTemplateOption[] {
  return securityInfos.filter((securityInfo) => {
    let result = true;
    securityInfoFilter.forEach(({ field, value }) => {
      if (field === 'tlsVersion') {
        const tlsVersionFilter = Number(securityInfo[field].split('TLSv')[1]);
        const tlsVersionValue = Number(value.split('TLSv')[1]);
        if (tlsVersionValue >= tlsVersionFilter) {
          result = result && true;
        }
      } else if (securityInfo[field] === value) {
        result = result && true;
      } else {
        result = false;
      }
    });
    return result;
  }).map((securityInfo) => {
    let label = securityInfo.name;

    if (securityInfo?.catype === 'XCCA') {
      label = `${securityInfo.name} (${securityInfo.aftype} - ${securityInfo.litype})`;
    }

    return {
      label,
      value: securityInfo.id,
    };
  });
}

function getSshSecurityInfoOptions(sshSecurityInfos: SshSecurityInfo[]): FormTemplateOption[] {
  return sshSecurityInfos.map(({ id }) => ({
    label: atob(id),
    value: id,
  }));
}

function getXtpIdOptions(xtpIdInfos: Xtp[]): FormTemplateOption[] {
  return xtpIdInfos.map((xtpIdInfo) => {
    const label = `${xtpIdInfo.lbId} - [${xtpIdInfo.ipAddr}]`;
    const value = xtpIdInfo.id;

    return {
      label,
      value,
    };
  });
}

function getXdpIdOptions(xdpIdInfos: Xdp[]): FormTemplateOption[] {
  return xdpIdInfos.map((xdpIdInfo) => {
    const label = `${xdpIdInfo.ipduId} - [${xdpIdInfo.ipAddr}]`;
    const value = xdpIdInfo.id;

    return {
      label,
      value,
    };
  });
}

function getEricssonLimsOptions(accessFunctions: AccessFunction[]): FormTemplateOption[] {
  return accessFunctions
    .filter(({ tag }) => tag === 'ERICSSON_LIMS')
    .map(({ name, provisioningInterfaces }) => {
      const afid = provisioningInterfaces?.[0]?.options?.find(({ key }) => key === 'afid')?.value.toString() ?? '';

      return {
        label: name,
        value: afid,
      };
    }).filter((option) => option.value !== '');
}

function cleanupPreviousSecurityInfoOption(templateSection: FormTemplateSection, fieldName: string): void {
  if (templateSection.fields[fieldName] == null) {
    return;
  }

  const { options } = templateSection.fields[fieldName];

  if (options == null || options.length === 0) {
    return;
  }

  const firstOptionValue = options[0].value;
  if (firstOptionValue === '' || firstOptionValue === 'Tk9ORQ') {
    templateSection.fields[fieldName].options = [options[0]];
  } else {
    templateSection.fields[fieldName].options = [];
  }
}

export function setOptionsForFields(
  template: FormTemplate,
  options: FormTemplateOption[] | FormTemplateOptionMap,
  fieldNames: string[],
): void {
  Object.keys(template).forEach((section: string) => {
    const templateSection = template[section];

    if (typeof templateSection === 'string') {
      return;
    }

    setOptionForTemplateSection(templateSection, options, fieldNames);
  });
}

export function setOptionForTemplateSection(
  templateSection: FormTemplateSection,
  options: FormTemplateOption[] | FormTemplateOptionMap,
  fieldNames: string[],
): void {
  if (!templateSection) {
    return;
  }

  const { fields } = templateSection;

  if (!fields) {
    return;
  }

  fieldNames.forEach((fieldName) => {
    const templateField = fields[fieldName];
    if (templateField == null || templateField?.options == null) {
      return;
    }

    const properOptions = getProperOptions(templateField, options);
    setOptionFormTemplateField(templateField, properOptions);
  });
}

function getProperOptions(
  templateField: FormTemplateField,
  options: FormTemplateOption[] | FormTemplateOptionMap,
): FormTemplateOption[] {
  let properOption: FormTemplateOption[] = [];

  if (Array.isArray(options)) {
    properOption = options;

  } else {
    properOption = getProperOptionsFromOptionsMap(templateField, options);
  }

  return properOption;
}

function getProperOptionsFromOptionsMap(
  templateField: FormTemplateField,
  options: FormTemplateOptionMap,
): FormTemplateOption[] {
  const defaultOptions = options.default ?? [];

  const properOption = options[templateField.optionsFormat ?? 'default'] ?? defaultOptions;

  if (templateField.optionsFormatValue == null) {
    return properOption;
  }

  const clonedOptions = cloneDeep(properOption);

  clonedOptions.forEach((option) => {
    const compiledLabelTemplate = lodashTemplate(option.label, TEMPLATE_SETTINGS);
    option.label = compiledLabelTemplate({
      value: templateField.optionsFormatValue,
    });
  });

  return clonedOptions;
}

function setOptionFormTemplateField(
  templateField: FormTemplateField,
  options: FormTemplateOption[],
) {
  const firstOptions = templateField.options?.[0];
  if (firstOptions) {
    templateField.options = [firstOptions];
  }

  if (options.length > 0) {
    templateField.options?.push(...options);

    if (templateField.type !== 'advancedSelect' || templateField.placeholder == null) {
      // Only set the initial value for advancedSelect if the placeholder is empty.
      // All other types should still set the initial value.
      if (!templateField.preventInitialSelection) {
        templateField.initial = templateField.options?.[0].value ?? '';
      }
    }

  } else {
    templateField.initial = '';
  }
}

function setFieldInitialValueForTemplateSection(
  templateSection: FormTemplateSection | undefined,
  initial: string,
  fieldNames: string[],
): void {
  if (!templateSection) {
    return;
  }

  const { fields } = templateSection;

  if (!fields || !initial) {
    return;
  }

  fieldNames.forEach((fieldName) => {
    const templateField = fields[fieldName];
    if (templateField == null) {
      return;
    }

    templateField.initial = initial;
  });
}

function setOptionForOwnIpAddr(template: FormTemplate, xdps: Xdp[]) {
  Object.keys(template).forEach((section: string) => {
    const templateSection = template[section];

    if (typeof templateSection === 'string') {
      return;
    }

    const { fields } = templateSection;
    const options = xdps.map((xdp) => {
      return {
        label: xdp.ipAddr,
        value: xdp.ipAddr,
      };
    });

    if (!fields) {
      return;
    }

    const templateField = fields.ownIPAddr;
    if (templateField == null || templateField?.options == null || templateField?.optionsBasedOn == null) {
      return;
    }
    const properOptions = getProperOptions(templateField, options);
    setOptionFormTemplateField(templateField, properOptions);
  });
}

function adaptTemplateForCopyMode(template: FormTemplate): FormTemplate {
  (template.general as FormTemplateSection).fields.tag.readOnly = true;

  return template;
}

function adaptTemplateForEditMode(template: FormTemplate): FormTemplate {
  attemptToMakeFieldReadOnlyFromSection(template, 'general', 'tag');
  attemptToMakeFieldReadOnlyFromSection(template, 'general', 'contentDeliveryVia');

  POSSIBLE_INTERFACES.forEach((interfaceName) => {
    attemptToShowFieldFromSection(template, interfaceName, 'state');
    attemptToMakeFieldReadOnlyFromSection(template, interfaceName, 'lbid');
  });

  if (template.key === 'ALU_DSS') {
    attemptToMakeFieldReadOnlyFromSection(template, 'systemInformation', 'model');
  }

  if (template.key === 'SONUS_SBC') {
    attemptToMakeFieldReadOnlyFromSection(template, 'provisioningInterfaces_directFromAF', 'secinfoid');
  }

  if (template.key === 'CISCO_ER') {
    attemptToMakeFieldReadOnlyFromSection(template, 'contentInterfaces_directFromAF', 'destPort');
    attemptToMakeFieldReadOnlyFromSection(template, 'contentInterfaces_directFromAF', 'destIPAddr');
    attemptToMakeFieldReadOnlyFromSection(template, 'contentInterfaces_directFromAF', 'ownnetid');
    attemptToMakeFieldReadOnlyFromSection(template, 'contentInterfaces_directFromAF', 'reqState');
  }

  return template;
}

function adaptTemplateForViewMode(template: FormTemplate): FormTemplate {
  setAllFieldsAsReadOnly(template);

  POSSIBLE_INTERFACES.forEach((interfaceName) => {
    attemptToShowFieldFromSection(template, interfaceName, 'state');
  });

  return template;
}

function setAllFieldsAsReadOnly(template: FormTemplate): void {
  Object.entries(template).forEach(([_templateKey, templateEntry]) => {
    if (typeof templateEntry === 'string') {
      return;
    }

    Object.entries(templateEntry.fields).forEach(([_fieldKey, fieldEntry]) => {
      fieldEntry.disabled = false;
      fieldEntry.readOnly = true;
    });
  });
}

function attemptToShowFieldFromSection(template: FormTemplate, templateSection: string, fieldName: string) {
  const formSection = (template[templateSection] as FormTemplateSection);
  if (formSection == null) {
    return;
  }

  const field = formSection.fields[fieldName];
  if (field == null) {
    return;
  }

  field.hide = 'false';
}

function attemptToMakeFieldReadOnlyFromSection(template: FormTemplate, templateSection: string, fieldName: string) {
  const formSection = (template[templateSection] as FormTemplateSection);
  if (formSection == null) {
    return;
  }

  const field = formSection.fields[fieldName];
  if (field == null) {
    return;
  }

  field.readOnly = true;
}

function attemptToMakeFieldDisabledFromSection(template: FormTemplate, templateSection: string, fieldName: string) {
  const formSection = (template[templateSection] as FormTemplateSection);
  if (formSection == null) {
    return;
  }

  const field = formSection.fields[fieldName];
  if (field == null) {
    return;
  }

  field.disabled = true;
}

function attemptToMakeFieldHiddenFromSection(template: FormTemplate, templateSection: string, fieldName: string) {
  const formSection = (template[templateSection] as FormTemplateSection);
  if (formSection == null) {
    return;
  }

  const field = formSection.fields[fieldName];
  if (field == null) {
    return;
  }

  field.hide = 'true';
}

function getSoftwareVersionOptions(key: string, enableAF: SupportedFeaturesAccessFunctions): FormTemplateOption[] {
  if (key === 'ERICSSON_NE' && enableAF) {
    const ericssonWebVersions = enableAF?.ERICSSON_LIMS?.versions || [];
    const ericssonCorbaVersions = enableAF?.ERICSSON_LIMS_CORBA?.versions || [];
    const combinedVersion = [...ericssonWebVersions, ...ericssonCorbaVersions];

    return combinedVersion.map((version) => ({
      label: version,
      value: version,
    }));
  }

  const accessFunction = enableAF[key];
  return accessFunction.versions.map((version) => ({
    label: version,
    value: version,
  }));
}

function getSoftwareModel(accessFunction: SupportedFeaturesAccessFunction): string {
  return accessFunction.models?.[0] ?? '';
}

function getModelNumbers(accessFunction: SupportedFeaturesAccessFunction): FormTemplateOption[] {
  return accessFunction.models.map((number) => ({
    label: number,
    value: number,
  }));
}

function getHTTPVersions(accessFunction: SupportedFeaturesAccessFunction): FormTemplateOption[] {
  return accessFunction.httpVersions?.map((version) => ({
    label: version,
    value: version,
  })) ?? [];
}

function getAfProtocolVersions(accessFunction: SupportedFeaturesAccessFunction): FormTemplateOption[] | undefined {
  return accessFunction.afProtocolVersions?.map((version) => ({
    label: version,
    value: version,
  })) ?? undefined;
}

function getHTTPDefaultVersion(accessFunction: SupportedFeaturesAccessFunction): string {
  return accessFunction.httpDefaultVersion ?? '';
}

function adaptNetworkIdFieldByFieldName(
  templateSection: FormTemplateSection,
  networkId: string,
  fieldName: 'ownnetid' | 'cdnetid',
): FormTemplateSection {
  const options = templateSection.fields[fieldName]?.options;
  if (options == null || networkId === '') {
    return templateSection;
  }

  const netIdOption = options.find(({ value }) => value === networkId);
  if (netIdOption != null) {
    return templateSection;
  }

  const clonedTemplateSection = cloneDeep(templateSection);

  if (clonedTemplateSection.fields[fieldName].type !== 'multiSelectCustom') {
    clonedTemplateSection.fields[fieldName].options?.push({
      label: 'Network ID not found',
      value: networkId,
    });
  }

  return clonedTemplateSection;
}

export function adaptTemplateInterfaceForDeliveryFormat(
  affectedFieldsOnChange: string[] | undefined,
  templateSection: FormTemplateSection | undefined,
  fieldValue: string,
  key: string,
): void {
  if (templateSection == null || affectedFieldsOnChange == null) {
    return;
  }

  switch (key) {
    case 'ERICSSON_LIMS_CORBA': {
      adaptTemplateInterfaceForDeliveryFormatForEricssonLimsCorba(templateSection, fieldValue);
      padRows(templateSection, [2, 3], 4);
      break;
    }

    case 'ERICSSON_LIMS': {
      adaptTemplateInterfaceForDeliveryFormatForEricssonLims(templateSection, fieldValue);
      padRows(templateSection, [2, 3], 4);
      break;
    }

    case 'VIETTEL_MD': {
      adaptTemplateInterfaceForDeliveryFormatForViettelMd(templateSection, fieldValue);
      padRows(templateSection, [2, 3], 4);
      break;
    }

    case 'SIEMENS_LIMS': {
      adaptTemplateInterfaceForDeliveryFormatForSiemensLims(templateSection, fieldValue);
      padRows(templateSection, [2, 3, 4], 4);
      break;
    }

    case 'COGNYTE': {
      adaptTemplateInterfaceForDeliveryFormatForCognyte(templateSection, fieldValue);
      padRows(templateSection, [2, 3], 4);
      break;
    }

    case 'VRNT_SG': {
      adaptTemplateInterfaceForDeliveryFormatForVerintStarGate(templateSection, fieldValue);
      padRows(templateSection, [2, 3], 4);
      break;
    }

    default: {
      break;
    }
  }

}

function adaptTemplateInterfaceForDeliveryFormatForEricssonLimsCorba(
  templateSection: FormTemplateSection,
  fieldValue: string
) {
  const {
    userPassword,
    remotePort,
    appprotocol,
    appprotocolver,
    naming,
    password
  } = templateSection.fields;
  if (fieldValue === 'TS201671') {
    userPassword.hide = 'false';
    password.hide = 'true';
    remotePort.hide = 'false';
    appprotocol.hide = 'false';
    appprotocolver.hide = 'false';
    password.hide = 'true';
    naming.hide = 'true';
  } else {
    userPassword.hide = 'true';
    password.hide = 'false';
    remotePort.hide = 'true';
    appprotocol.hide = 'true';
    appprotocolver.hide = 'true';
    password.hide = 'false';
    naming.hide = 'false';
  }
}

function adaptTemplateInterfaceForDeliveryFormatForEricssonLims(
  templateSection: FormTemplateSection,
  fieldValue: string
) {
  const {
    userPassword,
    remotePort,
    appprotocol,
    appprotocolver,
    naming,
    password
  } = templateSection.fields;
  if (fieldValue === 'TS201671') {
    userPassword.hide = 'false';
    password.hide = 'true';
    remotePort.hide = 'false';
    appprotocol.hide = 'false';
    appprotocolver.hide = 'false';
    password.hide = 'true';
    naming.hide = 'true';
  } else {
    userPassword.hide = 'true';
    password.hide = 'false';
    remotePort.hide = 'true';
    appprotocol.hide = 'true';
    appprotocolver.hide = 'true';
    password.hide = 'false';
    naming.hide = 'false';
  }
}

function adaptTemplateInterfaceForDeliveryFormatForSiemensLims(
  templateSection: FormTemplateSection,
  fieldValue: string
) {
  const {
    password,
    lea,
    leacs,
    leaps,
    appProtocol,
    version,
    userPassword,
    ftppassword
  } = templateSection.fields;
  if (fieldValue === 'TS201671') {
    leacs.hide = 'false';
    userPassword.hide = 'false';
    leaps.hide = 'true';
    lea.hide = 'true';
    password.hide = 'true';
  } else {
    password.hide = 'false';
    appProtocol.hide = 'true';
    version.hide = 'true';
    lea.hide = 'true';
    leaps.hide = 'false';
    leacs.hide = 'true';
    userPassword.hide = 'true';
    ftppassword.hide = 'true';
  }
}

function adaptTemplateInterfaceForDeliveryFormatForCognyte(
  templateSection: FormTemplateSection,
  fieldValue: string
) {
  const {
    password,
    userPassword,
    remotePort,
    appProtocol,
    version,
    naming
  } = templateSection.fields;

  if (fieldValue === 'TS201671') {
    userPassword.hide = 'false';
    password.hide = 'true';
    remotePort.hide = 'false';
    appProtocol.hide = 'false';
    version.hide = 'false';
    naming.hide = 'true';
  } else {
    userPassword.hide = 'true';
    password.hide = 'false';
    remotePort.hide = 'true';
    appProtocol.hide = 'true';
    version.hide = 'true';
    naming.hide = 'false';
  }
}

function adaptTemplateInterfaceForDeliveryFormatForVerintStarGate(
  templateSection: FormTemplateSection,
  fieldValue: string
) {
  const {
    password,
    userPassword,
    remotePort,
    appProtocol,
    version,
    naming
  } = templateSection.fields;

  if (fieldValue === 'TS201671') {
    userPassword.hide = 'false';
    password.hide = 'true';
    remotePort.hide = 'false';
    appProtocol.hide = 'false';
    version.hide = 'false';
    password.hide = 'true';
    naming.hide = 'true';
  } else {
    userPassword.hide = 'true';
    password.hide = 'false';
    remotePort.hide = 'true';
    appProtocol.hide = 'true';
    version.hide = 'true';
    password.hide = 'false';
    naming.hide = 'false';
  }
}

function adaptTemplateInterfaceForDeliveryFormatForViettelMd(
  templateSection: FormTemplateSection,
  fieldValue: string
) {
  const {
    password,
    userPassword,
    remotePort,
    appprotocol,
    appprotocolver
  } = templateSection.fields;

  if (fieldValue === 'TS201671') {
    userPassword.hide = 'false';
    password.hide = 'true';
    remotePort.hide = 'false';
    appprotocol.hide = 'false';
    appprotocolver.hide = 'false';
    password.hide = 'true';
  } else {
    userPassword.hide = 'true';
    password.hide = 'false';
    remotePort.hide = 'true';
    appprotocol.hide = 'true';
    appprotocolver.hide = 'true';
    password.hide = 'false';
  }
}

function adaptTemplateInterfaceForUri(
  affectedFieldsOnChange: string[] | undefined,
  templateSection: FormTemplateSection | undefined,
  fieldValue: string,
  key: string,
): void {
  if (templateSection == null || affectedFieldsOnChange == null) {
    return;
  }

  if (!affectedFieldsOnChange.includes('secinfoid')) {
    return;
  }

  const {
    secinfoid,
  } = templateSection.fields;

  if (secinfoid == null) {
    return;
  }

  if (fieldValue.toLocaleLowerCase().startsWith('http:')) {
    secinfoid.validation = {
      ...secinfoid.validation,
      required: false,
    };

    if (secinfoid.options != null) {

      const secinfoidValue = (key === 'VRNT_SG' || key === 'COGNYTE') ? '' : 'Tk9ORQ';

      secinfoid.initial = secinfoidValue;
      secinfoid.options[0] = {
        label: 'None',
        value: secinfoidValue,
      };
    }

  } else {
    secinfoid.validation = {
      ...secinfoid.validation,
      required: true,
    };

    if (secinfoid.options != null) {
      secinfoid.initial = '';
      secinfoid.options[0] = {
        label: 'None',
        value: '',
      };
    }
  }
}

function adaptTemplateInterfaceForSecuritylvl(
  affectedFieldsOnChange: string[] | undefined,
  templateSection: FormTemplateSection | undefined,
  fieldValue: string,
  key: string,
): void {
  if (templateSection == null || affectedFieldsOnChange == null) {
    return;
  }

  if (!affectedFieldsOnChange.includes('authpasswd') || !affectedFieldsOnChange.includes('privpasswd')) {
    return;
  }

  const { securitylvl, authpasswd, privpasswd } = templateSection.fields;

  if (securitylvl == null || authpasswd == null || privpasswd == null) {
    return;
  }

  if (fieldValue === 'NOAUTHNOPRIV') {
    authpasswd.disabled = true;
    authpasswd.validation = {
      ...authpasswd.validation,
      required: false,
    };

    privpasswd.disabled = true;
    privpasswd.validation = {
      ...privpasswd.validation,
      required: false,
    };
  }

  if (fieldValue === 'AUTHNOPRIV') {
    authpasswd.disabled = false;
    authpasswd.validation = {
      ...authpasswd.validation,
      required: true,
    };

    privpasswd.disabled = true;
    privpasswd.validation = {
      ...privpasswd.validation,
      required: false,
    };
  }

  if (fieldValue === 'AUTHPRIV') {
    authpasswd.disabled = false;
    authpasswd.validation = {
      ...authpasswd.validation,
      required: true,
    };

    privpasswd.disabled = false;
    privpasswd.validation = {
      ...privpasswd.validation,
      required: true,
    };
  }
}

function adaptTemplateInterfaceForMaxx2Conn(
  affectedFieldsOnChange: string[] | undefined,
  templateSection: FormTemplateSection | undefined,
  fieldValue: string,
): void {
  if (templateSection == null || affectedFieldsOnChange == null) {
    return;
  }

  if (!affectedFieldsOnChange.includes('destPort')) {
    return;
  }

  const {
    destPort,
  } = templateSection.fields;

  if (destPort == null) {
    return;
  }

  if (+fieldValue > 4) {
    destPort.disabled = true;
  } else {
    destPort.disabled = false;
  }
}

function adaptTemplateForTPKT(
  affectedFieldsOnChange: string[] | undefined,
  templateSection: FormTemplateSection | undefined,
  fieldValue: boolean | undefined,
): void {
  if (templateSection == null || affectedFieldsOnChange == null) {
    return;
  }

  if (!affectedFieldsOnChange.includes('x224headerpresent')) {
    return;
  }

  const {
    x224headerpresent,
  } = templateSection.fields;

  if (x224headerpresent == null) {
    return;
  }

  if (fieldValue ?? true) {
    x224headerpresent.readOnly = false;
  } else {
    x224headerpresent.readOnly = true;
  }
}
function adaptTemplateInterfaceForDeliveryVia(
  templateSection: FormTemplateSection | undefined,
  fieldValue: string,
): void {

  if (templateSection == null) {
    return;
  }

  const {
    deliveryVia,
    sharedsecret,
    version,
    dscp,
    secinfoid,
  } = templateSection.fields;

  if (deliveryVia == null) {
    return;
  }

  if (fieldValue === 'VoIP') {
    sharedsecret.hide = 'true';
    version.hide = 'true';
    dscp.hide = 'false';
    secinfoid.hide = 'false';
  } else if (fieldValue === 'PacketCable') {
    sharedsecret.hide = 'false';
    version.hide = 'false';
    dscp.hide = 'true';
    secinfoid.hide = 'true';
  }
}

function adaptTemplateForFtpType(
  affectedFieldsOnChange: string[] | undefined,
  templateSection: FormTemplateSection | undefined,
  fieldValue: string | undefined,
): void {
  if (templateSection == null || affectedFieldsOnChange == null) {
    return;
  }

  if (!affectedFieldsOnChange.includes('secinfoid')) {
    return;
  }

  const {
    secinfoid,
  } = templateSection.fields;

  if (secinfoid == null) {
    return;
  }

  if (fieldValue === 'FTP') {
    secinfoid.validation = {
      ...secinfoid.validation,
      required: false,
    };

    if (secinfoid.options != null) {
      secinfoid.initial = 'Tk9ORQ';
      secinfoid.options[0] = {
        label: 'None',
        value: 'Tk9ORQ',
      };
    }

  } else {
    secinfoid.validation = {
      ...secinfoid.validation,
      required: true,
    };

    if (secinfoid.options != null) {
      secinfoid.initial = '';
      secinfoid.options[0] = {
        label: 'None',
        value: '',
      };
    }
  }
}

export function getXcipioNodesByType(
  xcipioNodes: NodeData[],
  types: string[],
): FormTemplateOption[] {
  return xcipioNodes?.filter(({ nodeType }) => types.includes(nodeType)).map((field) => ({
    label: field.nodeName,
    value: field.nodeName,
  }));
}

function adaptTemplateForSecinfoid(
  template: FormTemplate | undefined,
  templateSection: FormTemplateSection | undefined,
  fieldValue: string,
): void {

  if (template == null || templateSection == null || template?.key !== 'VRNT_SG') {
    return;
  }

  const {
    encryption,
  } = templateSection.fields;

  if (encryption == null) {
    return;
  }

  if (fieldValue === 'Tk9ORQ' || fieldValue == null || fieldValue === '') {
    encryption.initial = 'DISABLED';

  } else {
    encryption.initial = 'ENABLED';
  }

}

export function adaptTemplateForAdvancedSettingsVisibility(
  template: FormTemplate | undefined,
  templateSection: FormTemplateSection | undefined,
  fieldValue: string,
): void {

  if (
    template == null ||
    templateSection == null ||
    (template?.key !== 'ERICSSON_LIMS' && template?.key !== 'SIEMENS_LIMS')
  ) {
    return;
  }
  if (
    templateSection?.metadata?.layout == null ||
    templateSection.metadata.layout.collapsible == null ||
    (template.dataInterfaces as FormTemplateSection)?.metadata?.layout == null
  ) {
    return;
  }

  if (fieldValue === 'TS33108') {
    templateSection.fields.naming.hide = 'false';
    templateSection.fields.appprotocol.hide = 'true';
    templateSection.fields.appprotocolver.hide = 'true';
    if (templateSection.metadata.layout.collapsible.rows[0].filter((row) => row === '$empty').length === 1) {
      templateSection.metadata.layout.collapsible.rows[0].push('$empty');
    }
    if (templateSection.metadata.layout.collapsible.rows[0].filter((row) => row === '$empty').length < 2) {
      templateSection.metadata.layout.collapsible.rows[0].push('$empty');
    }
  } else {
    if (templateSection.metadata.layout.collapsible.rows[0].filter((row) => row === '$empty').length >= 2) {
      templateSection.metadata.layout.collapsible.rows[0].pop();
    }
    templateSection.fields.appprotocol.hide = 'false';
    templateSection.fields.appprotocolver.hide = 'false';
    templateSection.fields.naming.hide = 'true';
  }

}

export function adaptTemplateForInterfacesTabChange(
  interfaceName: AccessFunctionInterfaceFormSectionType,
  data: Partial<AccessFunctionFormData>,
  template: FormTemplate | undefined,
  fieldName: keyof AccessFunctionInterfaceFormData,
  fieldValue: unknown,
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);

  if (fieldName === 'deliveryFormat') {
    clonedTemplate.formId = Date.now().toString();
  }

  return clonedTemplate;
}

function getDFNIOptions(
  dfnInterfaces: DeliveryFunctionNetworkInterface[],
): FormTemplateOption[] {
  return dfnInterfaces?.map((entry) => ({
    label: `${entry.interface}:${entry.ipaddr}`,
    value: entry.niid.toString(),
  }));
}

export function adaptNetTypeFiled(
  template: FormTemplate | undefined,
  accessFunction: AccessFunctionFormData,
): FormTemplate | undefined {
  if (template == null) {
    return template;
  }

  if (accessFunction.tag !== 'ERICSSON_NE') {
    return template;
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  adaptTemplateForNetTypeAndResVersion(
    clonedTemplate,
    'netype',
    (accessFunction?.eimsNetworkElement as AccessFunctionInterfaceFormData[])?.[0]?.netype as string,
  );

  return clonedTemplate;
}

export function adaptAuditTypeField(
  template: FormTemplate | undefined,
  accessFunction: AccessFunctionFormData,
): FormTemplate | undefined {
  if (template == null) {
    return template;
  }

  if (accessFunction.tag !== 'ERICSSON_IMS') {
    return template;
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  if ((clonedTemplate.systemInformation as FormTemplateSection)?.fields?.action != null) {
    (clonedTemplate.systemInformation as FormTemplateSection).fields.action.hide = 'false';
  }

  return clonedTemplate;
}

function adaptTemplateForNetTypeAndResVersion(
  template: FormTemplate | undefined,
  fieldName: keyof AccessFunctionInterfaceFormData,
  fieldValue: string,
): void {

  if (template == null || template.key !== 'ERICSSON_NE' || fieldName !== 'netype') {
    return;
  }

  if (fieldValue === 'MSC') {
    (template.eimsNetworkElement as FormTemplateSection).fields.resversion.initial = 'MSCR12';
    (template.eimsNetworkElement as FormTemplateSection).fields.resversion.options = [
      { label: 'MSCR12', value: 'MSCR12' },
      { label: 'MSCR13', value: 'MSCR13' },
      { label: 'MSCR13.1', value: 'MSCR13.1' },
    ];
  } else if (fieldValue === 'HLR') {
    (template.eimsNetworkElement as FormTemplateSection).fields.resversion.initial = 'NONE';
    (template.eimsNetworkElement as FormTemplateSection).fields.resversion.options = [
      { label: 'NONE', value: 'NONE' },
    ];
  } else if (fieldValue === 'GPRS' || fieldValue === 'OV_MOWLAN') {
    (template.eimsNetworkElement as FormTemplateSection).fields.resversion.initial = 'CGSN4';
    (template.eimsNetworkElement as FormTemplateSection).fields.resversion.options = [
      { label: 'CGSN4', value: 'CGSN4' },
      { label: 'CPG', value: 'CPG' },
      { label: 'GGSN', value: 'GGSN' },
      { label: 'MME', value: 'MME' },
    ];
  } else if (fieldValue === 'OV_GPRS') {
    (template.eimsNetworkElement as FormTemplateSection).fields.resversion.initial = 'GGSN';
    (template.eimsNetworkElement as FormTemplateSection).fields.resversion.options = [
      { label: 'CGSN4', value: 'CGSN4' },
      { label: 'CPG', value: 'CPG' },
      { label: 'GGSN', value: 'GGSN' },
      { label: 'MME', value: 'MME' },
    ];
  } else if (fieldValue === 'FIX') {
    (template.eimsNetworkElement as FormTemplateSection).fields.resversion.initial = 'TL5';
    (template.eimsNetworkElement as FormTemplateSection).fields.resversion.options = [
      { label: 'TL5', value: 'TL5' },
    ];
  } else if (fieldValue === 'MOWLAN') {
    (template.eimsNetworkElement as FormTemplateSection).fields.resversion.initial = 'NONE';
    (template.eimsNetworkElement as FormTemplateSection).fields.resversion.options = [
      { label: 'NONE', value: 'NONE' },
    ];
  } else if (fieldValue === 'SIPSERVER' || fieldValue === 'OV_SIPSERVER') {
    (template.eimsNetworkElement as FormTemplateSection).fields.resversion.initial = 'A-SBG15';
    (template.eimsNetworkElement as FormTemplateSection).fields.resversion.options = [
      { label: 'A-SBG15', value: 'A-SBG15' },
      { label: 'N-SBG15', value: 'N-SBG15' },
      { label: 'SBG16', value: 'SBG16' },
      { label: 'SBG17B', value: 'SBG17B' },
    ];
  } else {
    (template.eimsNetworkElement as FormTemplateSection).fields.resversion.options = [];
  }

  if (fieldValue === 'OV_SIPSERVER' || fieldValue === 'OV_MOWLAN' || fieldValue === 'OV_GPRS') {
    (template.eimsNetworkElement as FormTemplateSection).fields.isnegroup.initial = 'TRUE';
    (template.eimsNetworkElement as FormTemplateSection).fields.isnegroup.disabled = true;
  } else {
    (template.eimsNetworkElement as FormTemplateSection).fields.isnegroup.disabled = false;
  }

  template.formId = Date.now().toString();

}

function adaptTemplateForNodeNameType(
  template: FormTemplate | undefined,
  fieldName: keyof AccessFunctionInterfaceFormData,
  fieldValue: string,
  data: Partial<AccessFunctionFormData>,
  xcipioNodes?: NodeData[],
  templates?: { [key: string]: FormTemplate; } | undefined
): void {
  const tag = data.tag as string;

  if (template == null) {
    return;
  }

  if (fieldValue === 'ccnodename') {
    (template.provisioningInterfaces as FormTemplateSection).fields.ccnodename.hide = 'false';
    (template.provisioningInterfaces as FormTemplateSection).fields.mdf3NodeName.hide = 'true';
    if ((template.provisioningInterfaces as FormTemplateSection).fields.mdf3NodeNameMultiSelect) {
      (template.provisioningInterfaces as FormTemplateSection).fields.mdf3NodeNameMultiSelect.hide = 'true';
    }
    (template.contentInterfaces as FormTemplateSection).metadata.nodeNameField = 'ccnodename';
    (template.contentInterfaces as FormTemplateSection).fields.moduleName.options = moduleNameOptions(tag).ccnodename;
  } else if (fieldValue === 'mdf3NodeName') {
    let destinationSet = false;
    if (data.provisioningInterfaces && data.provisioningInterfaces[0]?.dsid_supported) {
      destinationSet = data.provisioningInterfaces && data.provisioningInterfaces[0]?.dsid_supported;
    }
    (template.provisioningInterfaces as FormTemplateSection).fields.ccnodename.hide = 'true';
    if (!destinationSet) {
      (template.provisioningInterfaces as FormTemplateSection).fields.mdf3NodeName.hide = 'false';
      (template.contentInterfaces as FormTemplateSection).metadata.nodeNameField = 'mdf3NodeName';
      (template.contentInterfaces as FormTemplateSection).fields.moduleName.options = moduleNameOptions(tag).mdf3NodeName;
    }
  }

  template.formId = Date.now().toString();
}

const moduleNameOptions = (templateTag: string) => {
  const tag = templateTag as 'CASA_5GSMF' | 'ERK_SMF' | 'ERK_5GSMF';

  const optionsMap = {
    CASA_5GSMF: {
      ccnodename: [
        { label: 'CASA5GUPFX3IM', value: 'CASA5GUPFX3IM' },
      ],
      mdf3NodeName: [
        { label: 'CASA5GUPFX3IM', value: 'CASA5GUPFX3IM' },
      ],
    },
    ERK_SMF: {
      ccnodename: [
        { label: 'ERKUPFX3IM', value: 'ERKUPFX3IM' },
      ],
      mdf3NodeName: [
        { label: 'ERKUPFX3IM', value: 'ERKUPFX3IM' },
      ],
    },
    ERK_5GSMF: {
      ccnodename: [
        { label: 'ERKUPFX3IM', value: 'ERKUPFX3IM' },
      ],
      mdf3NodeName: [
        { label: 'ERKUPFX3IM', value: 'ERKUPFX3IM' },
      ],
    },
  };

  return optionsMap[tag];
};

function adaptNodeNamesForSMF(
  tag: string,
  ccnodenameField: FormTemplateField,
  mdf3NodeNameField: FormTemplateField,
  nodeNameTypeField: FormTemplateField,
  moduleNameField: FormTemplateField,
  contentInterfacesMetadata: FormTemplateMetaData,
  templateSection: FormTemplateSection | undefined,
  ccnodenameValue: string,
  xcipioNodes?: NodeData[],
): void {

  if (templateSection == null || xcipioNodes == null) {
    return;
  }

  const isCCPAG = xcipioNodes?.find((node) => node.nodeName === ccnodenameValue)?.nodeType === 'CCPAG';

  if (isCCPAG) {
    ccnodenameField.initial = ccnodenameValue;
    ccnodenameField.hide = 'false';
    nodeNameTypeField.initial = 'ccnodename';
    mdf3NodeNameField.hide = 'true';
    moduleNameField.options = moduleNameOptions(tag).ccnodename;
    contentInterfacesMetadata.nodeNameField = 'ccnodename';
  } else {
    mdf3NodeNameField.initial = ccnodenameValue;
    mdf3NodeNameField.hide = 'false';
    nodeNameTypeField.initial = 'mdf3NodeName';
    ccnodenameField.initial = '';
    ccnodenameField.hide = 'true';
    moduleNameField.options = moduleNameOptions(tag)?.mdf3NodeName;
    contentInterfacesMetadata.nodeNameField = 'mdf3NodeName';
  }
}

export function getDistributorsTableNodeName(
  templateSection: FormTemplateSection | undefined,
  data: Partial<AccessFunctionFormData> | undefined,
  xcipioNodes?: NodeData[],
): string {
  const nodeNameField = templateSection?.metadata?.nodeNameField;

  if (nodeNameField === 'mdf3NodeName' && !data?.provisioningInterfaces?.[0]?.mdf3NodeName) {
    const ccnodenameValue = data?.provisioningInterfaces?.[0]?.ccnodename;
    const isCCPAG = (xcipioNodes || [])?.find((node) => node.nodeName === ccnodenameValue)?.nodeType === 'CCPAG';

    if (
      !isCCPAG
      && data?.provisioningInterfaces != null
      && data?.provisioningInterfaces.length
      && xcipioNodes?.length
    ) {
      data.provisioningInterfaces[0].mdf3NodeName = ccnodenameValue;
    }
  }

  // Give preference to multiSelect fields
  if (nodeNameField) {
    const nodeNamesX1 = data?.provisioningInterfaces?.[0]?.[`${nodeNameField}MultiSelect` as 'mdf2NodeNameMultiSelect' | 'mdf3NodeNameMultiSelect'] ?? [];
    if (nodeNamesX1.length > 0) {
      const spaceSeparatedNodeNamesX1 = (nodeNamesX1 || [])
        .map((x: FormTemplateOption) => x.value)
        .reduce((acc, curr) => {
          acc += ` ${curr}`;
          return acc;
        }, '' as string);

      return spaceSeparatedNodeNamesX1.substring(1);
    }

    const nodeNamesMultiSelect = data?.[`${nodeNameField}MultiSelect` as 'mdf2NodeNameMultiSelect' | 'mdf3NodeNameMultiSelect'] ?? [];
    if (nodeNamesMultiSelect.length > 0) {
      const spaceSeparatedNodeNames = (nodeNamesMultiSelect || [])
        .map((x: FormTemplateOption) => x.value)
        .reduce((acc, curr) => {
          acc += ` ${curr}`;
          return acc;
        }, '' as string);

      return spaceSeparatedNodeNames.substring(1);
    }

    const nodeName = data?.[`${nodeNameField}` as 'mdf2NodeName' | 'mdf3NodeName'];
    if (!_.isNil(nodeName)) {
      return nodeName;
    }

    const nodeNameX1 = data?.provisioningInterfaces?.[0]?.[`${nodeNameField}` as 'mdf2NodeName' | 'mdf3NodeName'];
    return nodeNameX1 || '';
  }

  // The Node Name is used to filter the list of Ericsson UPF distributors when is5gcVcpEnabled is true.
  // The Ericsson UPF distributors are visible inside the Ericsson SMF X3 section.
  return data?.provisioningInterfaces?.[0]?.ccnodename as string;
}

export function adaptTemplateX2Interface(
  templates: { [key: string]: FormTemplate },
  enabledFeatures: string[],
): { [key: string]: FormTemplate } {
  const isXcCnfvcpEnabled = enabledFeatures.includes('XC-CNFVCP');

  const adaptedTemplates: { [key: string]: FormTemplate } = {};

  Object.keys(templates).forEach((templateName: string) => {
    const template = cloneDeep(templates[templateName]);
    const templateKey = template.key;

    const mdf3NodeNameInX1DirectFromAF = (template.provisioningInterfaces_directFromAF as FormTemplateSection)?.fields?.mdf3NodeName;
    const mdf3NodeNameInX1 = (template.provisioningInterfaces as FormTemplateSection)?.fields?.mdf3NodeName;
    const mdf2NodeNameInX1 = (template.provisioningInterfaces as FormTemplateSection)?.fields?.mdf2NodeName;
    const mdf3NodeNameInX3 = (template.contentInterfaces as FormTemplateSection)?.fields?.mdf3NodeName;

    if (isXcCnfvcpEnabled) {
      if (templateKey === 'SONUS_SBC') {
        delete template.dataInterfaces_directFromAF;
        if (mdf3NodeNameInX1DirectFromAF) {
          mdf3NodeNameInX1DirectFromAF.hide = 'false';
        }
        (template.provisioningInterfaces_directFromAF as FormTemplateSection).fields.ccdestip.type = 'select';
      } else if (templateKey === 'STA_GGSN') {
        if (mdf3NodeNameInX1DirectFromAF) {
          mdf3NodeNameInX1DirectFromAF.hide = 'false';
        }
        (template.provisioningInterfaces_directFromAF as FormTemplateSection).fields.ccdestip.type = 'select';
      } else if (templateKey === 'NSN_CSCF') {
        if (mdf3NodeNameInX1) {
          mdf3NodeNameInX1.hide = 'true';
        }
        if (mdf2NodeNameInX1) {
          mdf2NodeNameInX1.hide = 'true';
        }
        (template.provisioningInterfaces as FormTemplateSection).fields.ccdestip.type = 'text';
      }
    } else if (templateKey === 'SONUS_SBC') {
      if (mdf3NodeNameInX1DirectFromAF) {
        mdf3NodeNameInX1DirectFromAF.hide = 'true';
      }
      (template.provisioningInterfaces_directFromAF as FormTemplateSection).fields.ccdestip.type = 'text';
    } else if (templateKey === 'STA_GGSN') {
      if (mdf3NodeNameInX1DirectFromAF) {
        mdf3NodeNameInX1DirectFromAF.hide = 'true';
      }
      (template.provisioningInterfaces_directFromAF as FormTemplateSection).fields.ccdestip.type = 'text';
    } else if (templateKey === 'ACME_X123') {
      if (mdf3NodeNameInX3) {
        mdf3NodeNameInX3.hide = 'true';
      }
    }

    adaptedTemplates[templateKey] = template;
  });

  return adaptedTemplates;
}

export function adaptDiameterField(
  template: FormTemplate | undefined,
  diameter: Diameter | undefined,
  formData?: AccessFunctionFormData,
  viewMode?: boolean,
): FormTemplate | undefined {

  if (template == null || diameter == null) {
    return template;
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  const {
    id,
    dsid,
    interfaceValue,
    niid,
    port,
    orighost,
    origrealm,
    secinfoid,
    encryption,
  } = diameter;

  setDiameterOptionValue(clonedTemplate, 'diameter', 'diameterId', id, true);
  setDiameterOptionValue(clonedTemplate, 'diameter', 'dsid', dsid, true);
  setDiameterOptionValue(clonedTemplate, 'diameter', 'interface', interfaceValue, true);
  setDiameterOptionValue(clonedTemplate, 'diameter', 'encryption', encryption, viewMode);
  setDiameterOptionValue(clonedTemplate, 'diameter', 'orighost', orighost, viewMode);
  setDiameterOptionValue(clonedTemplate, 'diameter', 'origrealm', origrealm, viewMode);
  setDiameterOptionValue(clonedTemplate, 'diameter', 'niid', niid, viewMode);
  setDiameterOptionValue(clonedTemplate, 'diameter', 'port', port, viewMode);
  setDiameterOptionValue(clonedTemplate, 'diameter', 'secinfoid', secinfoid, viewMode);

  clonedTemplate.formId = Date.now().toString();

  return clonedTemplate;
}

export function getDsidAfOptions(
  accessFunctions: AccessFunction[],
  formData?: AccessFunctionFormData
): { [key: string]: string } | undefined {

  if (formData != null) {
    return ((formData as unknown) as { [key: string]: string });
  }

  const dsidAccessFunction = accessFunctions.find(selectAfWithDsid);

  if (dsidAccessFunction == null) {
    return undefined;
  }

  const firstDataInterfaces = dsidAccessFunction?.dataInterfaces?.[0];
  const firstDataInterfacesOptions = firstDataInterfaces?.additionalMOs?.find((mo) => mo.moName === 'DIAMETER')?.options;

  if (firstDataInterfacesOptions == null) {
    return undefined;
  }

  const options = firstDataInterfacesOptions.reduce((total, op) => ({
    ...total,
    [op.key]: op.value,
  }), {});

  return {
    ...options,
    diameterId: firstDataInterfaces?.additionalMOs?.[0]?.id || '',
  };
}

export function selectAfWithDsid(accessFunction: AccessFunction) {
  const additionalMO = accessFunction?.dataInterfaces?.[0]?.additionalMOs?.[0];

  return additionalMO?.id != null && additionalMO?.moName === 'DIAMETER';
}

function setDiameterOptionValue(template: FormTemplate, templateSection: string, fieldName: string, fieldValue: string, readOnly?: boolean, disabled?: boolean) {
  const formSection = (template[templateSection] as FormTemplateSection);
  if (formSection == null) {
    return;
  }

  const field = formSection.fields[fieldName];

  if (field == null) {
    return;
  }

  field.initial = fieldValue;

  if (disabled) {
    field.disabled = disabled;
  }
  if (readOnly) {
    field.readOnly = readOnly;
  }
}

export function adaptTemplateInterfaceAxsgp(
  template: FormTemplate | undefined,
  fieldName: keyof AccessFunctionInterfaceFormData,
  fieldValue: string,
  templates: { [key: string]: FormTemplate },
): FormTemplate | undefined {

  if (template == null) {
    return;
  }

  if (fieldName !== 'version' || template?.key !== 'AXSGP') {
    return cloneDeep(template);
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  if ((clonedTemplate.systemInformation as FormTemplateSection).fields?.model?.options != null) {
    const options = getModelBasedOnVersion(templates[template?.key], fieldValue);
    (clonedTemplate.systemInformation as FormTemplateSection).fields.model.initial = options?.[0].value;
    (clonedTemplate.systemInformation as FormTemplateSection).fields.model.options = options;
  }

  (clonedTemplate.contentInterfaces_directFromAF as FormTemplateSection).fields.type.options = getTrafficTypeOptionsBasedOnVersion(templates[template?.key], fieldValue);

  return clonedTemplate;
}

export function setAxsgpModelOptions(
  template: FormTemplate | undefined,
  options: FormTemplateOption[],
): FormTemplate | undefined {
  if (template == null) {
    return;
  }
  if ((template.systemInformation as FormTemplateSection).fields?.model?.options != null) {
    (template.systemInformation as FormTemplateSection).fields.model.initial = options?.[0]?.value;
    (template.systemInformation as FormTemplateSection).fields.model.options = options;
  }
}

export function removeInterface(
  template: FormTemplate | undefined,
  interfaceName: string,
): void {
  if (template) {
    delete template[interfaceName];
  }
}

export function getModelBasedOnVersion(originalTemplate: FormTemplate, version: string): FormTemplateOption[] {
  if (originalTemplate == null) {
    return [];
  }
  const allOptions = (originalTemplate?.systemInformation as FormTemplateSection)?.fields?.model?.allOptions ?? [];
  const optionsMap = (originalTemplate?.systemInformation as FormTemplateSection)?.fields?.model?.optionsMap ?? {};

  const matchingKey = Object.keys(optionsMap).find((k) => new RegExp(k).test(version)) ?? 'other';
  const mappedOptions = optionsMap?.[matchingKey] ?? [];

  return allOptions.filter((option) => mappedOptions.includes(option.value));
}

export function getTrafficTypeOptionsBasedOnVersion(originalTemplate: FormTemplate, version: string): FormTemplateOption[] {
  if (originalTemplate == null) {
    return [];
  }
  const allOptions = (originalTemplate?.contentInterfaces_directFromAF as FormTemplateSection)?.fields?.type?.options ?? [];

  if (version === '4.7') {
    return allOptions.filter((option) => option.label === 'RADIUS');
  }
  return cloneDeep(allOptions);
}

export function adaptTemplateInterfaceForAXSGPModel(
  template: FormTemplate | undefined,
  fieldName: keyof AccessFunctionInterfaceFormData,
  fieldValue: string,
): FormTemplate | undefined {

  if (template == null) {
    return;
  }

  if (fieldName !== 'model' || template?.key !== 'AXSGP') {
    return cloneDeep(template);
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  if (clonedTemplate.contentInterfaces_directFromAF != null) {
    if (fieldValue === 'AXS-7002' || fieldValue === 'AXS-6002') {
      (clonedTemplate.contentInterfaces_directFromAF as FormTemplateSection).metadata.hide = 'true';
    } else {
      (clonedTemplate.contentInterfaces_directFromAF as FormTemplateSection).metadata.hide = 'false';
    }
    clonedTemplate.formId = Date.now().toString();
  }

  return clonedTemplate;
}

export function adaptTemplateForEricssonNE(
  template: FormTemplate | undefined,
  accessFunctions: AccessFunction[] | undefined,
  formData?: AccessFunctionFormData,
): FormTemplate | undefined {
  if (template && (template.key === 'ERICSSON_NE')) {
    const clonedTemplate = cloneDeep(template);
    const parentid = (clonedTemplate.eimsNetworkElement as FormTemplateSection).fields?.parentid?.options?.[0];
    if (!parentid) return template;

    const parentAF = accessFunctions?.find((accessFunction) => {
      const afid = accessFunction.provisioningInterfaces?.[0]?.options?.find(({ key }) => key === 'afid')?.value.toString() ?? ''
      return afid === parentid.value;
    });
    if (parentAF) {
      (clonedTemplate.systemInformation as FormTemplateSection).fields.version.initial = parentAF.version;
      (clonedTemplate.systemInformation as FormTemplateSection).fields.model.initial = parentAF.model;
    }

    if (formData && formData.eimsNetworkElement) {
      const neType = formData.eimsNetworkElement[0].netype;
      if (neType === 'OV_SIPSERVER' || neType === 'OV_MOWLAN' || neType === 'OV_GPRS') {
        (clonedTemplate.eimsNetworkElement as FormTemplateSection).fields.isnegroup.initial = 'TRUE';
        (clonedTemplate.eimsNetworkElement as FormTemplateSection).fields.isnegroup.disabled = true;
      }
    }

    return clonedTemplate;
  }
  return template;
}

export function adaptTemplateDeliveryFormatForEricssonLiims(
  templateData: FormTemplate | undefined,
  formData: AccessFunctionFormData
): FormTemplate | undefined {
  if (!templateData || !formData) {
    return templateData;
  }
  if (![
    'ERICSSON_LIMS',
    'ERICSSON_LIMS_CORBA',
    'VIETTEL_MD',
    'SIEMENS_LIMS',
    'COGNYTE',
    'VRNT_SG'
  ].includes(templateData.key)) {
    return templateData;
  }

  const dataInterface = templateData.dataInterfaces as FormTemplateSection;
  const affectedFields = dataInterface.fields.deliveryFormat.affectedFieldsOnChange;
  // Xcipio only support single delivery format so the deliveryFormat value belong to AF form data directly.
  const deliveryFormatValue = formData.deliveryFormat as string;
  adaptTemplateInterfaceForDeliveryFormat(affectedFields, dataInterface, deliveryFormatValue, templateData.key);

  return templateData;
}

export function adaptTemplateForUtimaco(
  template: FormTemplate | undefined): FormTemplate | undefined {
  if (template && (template.key === 'SIEMENS_LIMS')) {
    const clonedTemplate = cloneDeep(template);
    const afVersion = (clonedTemplate.systemInformation as FormTemplateSection).fields.version.initial;
    (clonedTemplate.provisioningInterfaces as FormTemplateSection).fields.version.initial = afVersion;

    return clonedTemplate;
  }
  return template;
}

export function adaptTemplateForUtimacoMCTypeChange(
  template: FormTemplate | undefined,
  formData?: AccessFunctionFormData
): FormTemplate | undefined {
  let clonedTemplate = cloneDeep(template);
  if (template && (template.key === 'SIEMENS_LIMS') && formData && formData?.dataInterfaces) {
    const mctype = formData?.dataInterfaces[0]?.mctype;
    const deliveryFormat = formData?.dataInterfaces[0]?.deliveryFormat;
    // set the template base on the first data Interface
    clonedTemplate = adaptTemplateMCTypeField(clonedTemplate, 'dataInterfaces', 'mctype', mctype, deliveryFormat)
  }
  return clonedTemplate;
}

export function adaptTemplateForNokiaCMG(
  template?: FormTemplate,
  modelNumber?: string,
  configNokiaCmgAf?: SupportedFeaturesAccessFunction,
): FormTemplate | undefined {
  if (template && (template.key === 'NOKIA_CMG')) {
    const clonedTemplate = cloneDeep(template);

    // Set model options
    if (configNokiaCmgAf) {
      const models = configNokiaCmgAf.models || [];
      if (models.some((value) => value === 'SMF_C')) {
        (clonedTemplate.systemInformation as FormTemplateSection).fields.model.initial = 'SMF_C'
      } else {
        (clonedTemplate.systemInformation as FormTemplateSection).fields.model.initial = models.length ? models[0] : '';
      }

      const modelOptions = models.map((model) => ({
        label: model,
        value: model,
      }));

      (clonedTemplate.systemInformation as FormTemplateSection).fields.model.options = modelOptions;
    }

    // Adapt visibility of PFCP LI Shared Key
    const clonedModelNumber = modelNumber ?? (clonedTemplate.systemInformation as FormTemplateSection).fields.model.initial;

    const versionList = (clonedTemplate.systemInformation as FormTemplateSection).fields.version.options;
    if (versionList?.some((value) => value.label === 'R24')) {
      (clonedTemplate.systemInformation as FormTemplateSection).fields.version.initial = 'R24';
    }

    const softwareVersion = (clonedTemplate.systemInformation as FormTemplateSection).fields.version.initial;
    (clonedTemplate.provisioningInterfaces as FormTemplateSection).fields.version.initial = softwareVersion;

    const templateSectionProvisioningInterfaces = (clonedTemplate.provisioningInterfaces as FormTemplateSection);
    const pfcplisharedkeyField = templateSectionProvisioningInterfaces?.fields?.pfcplisharedkey;
    const templateLayoutProvisioningInterfaces = templateSectionProvisioningInterfaces?.metadata?.layout?.collapsible;

    if (clonedModelNumber === 'CMG_C') {
      pfcplisharedkeyField.hide = 'false';

      if (templateLayoutProvisioningInterfaces) {
        const indexEmpty = templateLayoutProvisioningInterfaces?.rows[0].findIndex((field: string) => field === '$empty');
        if (indexEmpty > -1) { templateLayoutProvisioningInterfaces.rows[0][indexEmpty] = 'pfcplisharedkey'; }
      }

    } else {
      pfcplisharedkeyField.hide = 'true';

      if (templateLayoutProvisioningInterfaces) {
        const indexPfcplisharedkey = templateLayoutProvisioningInterfaces?.rows[0].findIndex((field: string) => field === 'pfcplisharedkey');
        if (indexPfcplisharedkey > -1) { templateLayoutProvisioningInterfaces.rows[0][indexPfcplisharedkey] = '$empty'; }
      }
    }

    const afx1servertlsprofileField = templateSectionProvisioningInterfaces?.fields?.afx1servertlsprofile;
    const aflit3ipaddrField = templateSectionProvisioningInterfaces?.fields?.aflit3ipaddr;
    const aflit3portField = templateSectionProvisioningInterfaces?.fields?.aflit3port;
    if (clonedModelNumber === 'SMF_C') {
      afx1servertlsprofileField.hide = 'false';
      aflit3ipaddrField.hide = 'false';
      aflit3portField.hide = 'false';
    } else {
      afx1servertlsprofileField.hide = 'true';
      aflit3ipaddrField.hide = 'true';
      aflit3portField.hide = 'true';
    }

    clonedTemplate.formId = Date.now().toString();
    return clonedTemplate;
  }

  return template;
}

export function adaptTemplateForNokiaCMGU(
  template?: FormTemplate,
  configNokiaCmgUAf?: SupportedFeaturesAccessFunction,
  modelNumber?: string,
): FormTemplate | undefined {
  if (template && (template.key === 'NOKIA_CMG_U')) {
    const clonedTemplate = cloneDeep(template);
    const versionList = (clonedTemplate.systemInformation as FormTemplateSection).fields.version.options;
    if (versionList?.some((value) => value.label === 'R24')) {
      (clonedTemplate.systemInformation as FormTemplateSection).fields.version.initial = 'R24';
    }
    const softwareVersion = (clonedTemplate.systemInformation as FormTemplateSection).fields.version.initial;

    (clonedTemplate.provisioningInterfaces as FormTemplateSection).fields.version.initial = softwareVersion;

    // Set model options
    if (configNokiaCmgUAf) {
      const models = configNokiaCmgUAf.models || [];
      if (models.some((value) => value === 'UPF_U')) {
        (clonedTemplate.systemInformation as FormTemplateSection).fields.model.initial = 'UPF_U'
      } else {
        (clonedTemplate.systemInformation as FormTemplateSection).fields.model.initial = models.length ? models[0] : '';
      }
      const modelOptions = models.map((model) => ({
        label: model,
        value: model,
      }));

      (clonedTemplate.systemInformation as FormTemplateSection).fields.model.options = modelOptions;
    }

    const templateSectionProvisioningInterfaces = (clonedTemplate.provisioningInterfaces as FormTemplateSection);
    const clonedModelNumber = modelNumber ?? (clonedTemplate.systemInformation as FormTemplateSection).fields.model.initial;
    const afx1servertlsprofileField = templateSectionProvisioningInterfaces?.fields?.afx1servertlsprofile;
    const afnfidField = templateSectionProvisioningInterfaces?.fields?.afnfid;
    const aflit3ipaddrField = templateSectionProvisioningInterfaces?.fields?.aflit3ipaddr;
    const aflit3portField = templateSectionProvisioningInterfaces?.fields?.aflit3port;
    const afx3srcipaddrField = templateSectionProvisioningInterfaces?.fields?.afx3srcipaddr;
    const pfcplisharedkeyField = templateSectionProvisioningInterfaces?.fields?.pfcplisharedkey;
    const aflit3upfpeeridField = templateSectionProvisioningInterfaces?.fields?.aflit3upfpeerid;
    const aflit3routerField = templateSectionProvisioningInterfaces?.fields?.aflit3router;
    const aflix1localipaddrField = templateSectionProvisioningInterfaces?.fields?.aflix1localipaddr;
    const aflix1localportField = templateSectionProvisioningInterfaces?.fields?.aflix1localport;

    if (clonedModelNumber === 'UPF_U') {
      if (afx1servertlsprofileField) { afx1servertlsprofileField.hide = 'false'; }
      if (afnfidField) { afnfidField.hide = 'false'; }
      if (aflit3ipaddrField) { aflit3ipaddrField.hide = 'false'; }
      if (aflit3portField) { aflit3portField.hide = 'false'; }
      if (aflit3upfpeeridField) { aflit3upfpeeridField.hide = 'false'; }
      if (afx3srcipaddrField) { afx3srcipaddrField.hide = 'true'; }
      if (pfcplisharedkeyField) { pfcplisharedkeyField.hide = 'true'; }
      if (aflit3routerField) { aflit3routerField.hide = 'false'; }
      if (aflix1localipaddrField) { aflix1localipaddrField.hide = 'false'; }
      if (aflix1localportField) { aflix1localportField.hide = 'false'; }
    } else {
      if (afx1servertlsprofileField) { afx1servertlsprofileField.hide = 'true'; }
      if (afnfidField) { afnfidField.hide = 'true'; }
      if (aflit3ipaddrField) { aflit3ipaddrField.hide = 'true'; }
      if (aflit3portField) { aflit3portField.hide = 'true'; }
      if (aflit3upfpeeridField) { aflit3upfpeeridField.hide = 'true'; }
      if (afx3srcipaddrField) { afx3srcipaddrField.hide = 'false'; }
      if (pfcplisharedkeyField) { pfcplisharedkeyField.hide = 'false'; }
      if (aflit3routerField) { aflit3routerField.hide = 'true'; }
      if (aflix1localipaddrField) { aflix1localipaddrField.hide = 'true'; }
      if (aflix1localportField) { aflix1localportField.hide = 'true'; }
    }

    clonedTemplate.formId = Date.now().toString();
    return clonedTemplate;
  }

  return template;
}

export function adaptTemplateForNokiaSMFAdmin(
  template?: FormTemplate,
  modelNumber?: string,
  configNokiaCmgAf?: SupportedFeaturesAccessFunction,
): FormTemplate | undefined {
  if (template && (template.key === 'NOKIA_SMF_ADMIN')) {
    const clonedTemplate = cloneDeep(template);

    // Set model options
    if (configNokiaCmgAf) {
      const models = configNokiaCmgAf.models || [];
      if (models.some((value) => value === 'SMF_C')) {
        (clonedTemplate.systemInformation as FormTemplateSection).fields.model.initial = 'SMF_C'
      } else {
        (clonedTemplate.systemInformation as FormTemplateSection).fields.model.initial = models.length ? models[0] : '';
      }

      const modelOptions = models.map((model) => ({
        label: model,
        value: model,
      }));

      (clonedTemplate.systemInformation as FormTemplateSection).fields.model.options = modelOptions;
    }

    const versionList = (clonedTemplate.systemInformation as FormTemplateSection).fields.version.options;
    if (versionList?.some((value) => value.label === 'R24')) {
      (clonedTemplate.systemInformation as FormTemplateSection).fields.version.initial = 'R24';
    }

    const softwareVersion = (clonedTemplate.systemInformation as FormTemplateSection).fields.version.initial;
    (clonedTemplate.provisioningInterfaces as FormTemplateSection).fields.version.initial = softwareVersion;

    clonedTemplate.formId = Date.now().toString();
    return clonedTemplate;
  }

  return template;
}

export function adaptTemplateForETSI103221(
  isIdentifierAssociationExtensionsEnabled: boolean,
  template?: FormTemplate,
  configETSI103221?: SupportedFeaturesAccessFunction,
  selectedModel?: string,
): FormTemplate | undefined {
  if (template && (template.key === 'ETSI103221')) {
    const clonedTemplate = cloneDeep(template);
    const model = selectedModel ?? (clonedTemplate.systemInformation as FormTemplateSection).fields.model.initial;
    const modelKey = String(model || '');

    if (configETSI103221) {
      const modelOptions = configETSI103221.modelOptions || {};
      const shouldSupportX2 = modelOptions[modelKey]?.supportX2 || false;
      const shouldSupportX3 = modelOptions[modelKey]?.supportX3 || false;

      if (shouldSupportX2) {
        (clonedTemplate.provisioningInterfaces as FormTemplateSection).fields.mdf2NodeName.hide = 'false';
      } else {
        (clonedTemplate.provisioningInterfaces as FormTemplateSection).fields.mdf2NodeName.hide = 'true';
      }

      if (shouldSupportX3) {
        (clonedTemplate.provisioningInterfaces as FormTemplateSection).fields.mdf3NodeName.hide = 'false';
      } else {
        (clonedTemplate.provisioningInterfaces as FormTemplateSection).fields.mdf3NodeName.hide = 'true';
      }
    }

    const provisioningInterface = (clonedTemplate.provisioningInterfaces as FormTemplateSection);
    if (isIdentifierAssociationExtensionsEnabled && (modelKey.endsWith('MME') || modelKey.endsWith('AMF'))) {
      provisioningInterface.fields.id_assoc_extensions.hide = 'false';
    } else {
      provisioningInterface.fields.id_assoc_extensions.hide = 'true';
    }

    clonedTemplate.formId = Date.now().toString();
    return clonedTemplate;
  }

  return template;
}

export function adaptTemplateForAMFIdentifierAssociation(
  isIdentifierAssociationExtensionsEnabled: boolean,
  template?: FormTemplate,
  model?: string
): FormTemplate | undefined {
  if (!template || !isIdentifierAssociationExtensionsEnabled || (template.key !== 'ERK_5GAMF' && template.key !== 'NOK_5GAMF' && template.key !== 'ETSI103221')) {
    return template;
  }

  const clonedTemplate = cloneDeep(template);
  const provisioningInterface = (clonedTemplate.provisioningInterfaces as FormTemplateSection);

  if (template.key === 'ERK_5GAMF') {
    padRows(provisioningInterface, [3], 4);
  }

  if (template.key === 'NOK_5GAMF') {
    padRows(provisioningInterface, [2], 4);
  }

  if (template.key === 'ETSI103221') {
    padRows(provisioningInterface, [2], 4);

    const modelKey = model ?? (clonedTemplate.systemInformation as FormTemplateSection).fields.model?.initial as string;
    if (isIdentifierAssociationExtensionsEnabled && modelKey && (modelKey.endsWith('MME') || modelKey.endsWith('AMF'))) {
      provisioningInterface.fields.id_assoc_extensions.hide = 'false';
    } else {
      provisioningInterface.fields.id_assoc_extensions.hide = 'true';
    }
  }

  return clonedTemplate;
}

export function adaptTemplateAdvancedSettingForEricssonWebService(
  softwareVersion: string | undefined,
  template?: FormTemplate,
): FormTemplate | undefined {
  if (!template || template.key !== 'ERICSSON_LIMS' || !softwareVersion) {
    return template;
  }
  const clonedTemplate = cloneDeep(template);
  const provisioningInterface = (clonedTemplate.provisioningInterfaces as FormTemplateSection);
  provisioningInterface.fields.appprotocolver.initial = softwareVersion;
  return clonedTemplate;
}

export function adaptTemplateDeliveryFormatField(
  template: FormTemplate | undefined,
  formSection: string,
  fieldName: string,
  value: string,
): FormTemplate | undefined {
  if (!template) {
    return template;
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  return clonedTemplate;
}

export function adaptTemplateMCTypeField(
  template: FormTemplate | undefined,
  formSection: string,
  fieldName: string,
  value?: string | number | boolean | string[] | undefined,
  deliveryFormat?: string
): FormTemplate | undefined {
  if (!template) {
    return template;
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  const dataInterfacesFields = (clonedTemplate.dataInterfaces as FormTemplateSection)?.fields;

  if (formSection === 'dataInterfaces' && fieldName === 'mctype') {
    if (!value) {
      value = dataInterfacesFields.mctype.initial;
    }

    if (dataInterfacesFields && value === 'FTP') {
      const affectedFields = (template.dataInterfaces as FormTemplateSection).fields.deliveryFormat.affectedFieldsOnChange;
      adaptTemplateInterfaceForDeliveryFormat(affectedFields, clonedTemplate.dataInterfaces as FormTemplateSection, deliveryFormat ?? '', (template as FormTemplate).key);
    } else {
      dataInterfacesFields.userName.hide = 'true';
      dataInterfacesFields.userPassword.hide = 'true';
      dataInterfacesFields.password.hide = 'true';
      dataInterfacesFields.destPort.hide = 'true';
      dataInterfacesFields.destIPAddr.hide = 'true';
      dataInterfacesFields.leacs.hide = 'true';
      dataInterfacesFields.leaps.hide = 'true';
      dataInterfacesFields.appProtocol.hide = 'true';
      dataInterfacesFields.deliveryFormat.hide = 'true';
      dataInterfacesFields.version.hide = 'true';
      dataInterfacesFields.ownnetid.hide = 'true';
      dataInterfacesFields.ftpusername.hide = 'true';
      dataInterfacesFields.ftppassword.hide = 'true';
      dataInterfacesFields.ipaddr.hide = 'false';
      dataInterfacesFields.port.hide = 'false';
      dataInterfacesFields.lea.hide = 'false';
      padRows((clonedTemplate.dataInterfaces as FormTemplateSection), [1, 2, 3, 4], 4);
    }
  }

  return clonedTemplate;
}

export function padRow(
  fields: FormTemplateFieldMap,
  row: string[],
  maxLength: number
) {
  const nonEmptyFields = (row ?? []).filter((x) => x !== '$empty').slice();
  const visibleFields = nonEmptyFields.filter((x) => fields[x]?.hide !== 'true');
  if (visibleFields.length < maxLength) {
    for (let ind = 0; ind < maxLength - visibleFields.length; ind += 1) {
      nonEmptyFields.push('$empty');
    }
  }
  return nonEmptyFields;
}

export function padRows(
  templateSection: FormTemplateSection,
  rows: number[],
  maxRowLength: number
) {
  rows.forEach((r) => {
    (templateSection.metadata.layout?.rows as string[][])[r] = padRow(
      templateSection.fields,
      (templateSection.metadata.layout?.rows as string[][])[r],
      maxRowLength
    );
  });
}

export function adaptTemplateForCiscoSii(
  template: FormTemplate | undefined,
  formData?: AccessFunctionFormData
): FormTemplate | undefined {
  const clonedTemplate = cloneDeep(template);

  if (template && (template.key === 'CISCO_ER') && formData && formData?.provisioningInterfaces) {
    const securityLevel = formData?.provisioningInterfaces[0]?.securitylvl;

    if (clonedTemplate) {
      if (securityLevel === 'NOAUTHNOPRIV') {
        (clonedTemplate.provisioningInterfaces_directFromAF as FormTemplateSection).fields.authpasswd.disabled = true;
        (clonedTemplate.provisioningInterfaces_directFromAF as FormTemplateSection).fields.privpasswd.disabled = true;
      } else if (securityLevel === 'AUTHNOPRIV') {
        (clonedTemplate.provisioningInterfaces_directFromAF as FormTemplateSection).fields.authpasswd.disabled = false;
        (clonedTemplate.provisioningInterfaces_directFromAF as FormTemplateSection).fields.privpasswd.disabled = true;
      } else if (securityLevel === 'AUTHPRIV') {
        (clonedTemplate.provisioningInterfaces_directFromAF as FormTemplateSection).fields.authpasswd.disabled = false;
        (clonedTemplate.provisioningInterfaces_directFromAF as FormTemplateSection).fields.privpasswd.disabled = false;
      }
    }
  }
  return clonedTemplate;
}
